import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import http from '../utils/request';
import { motion, AnimatePresence } from 'framer-motion';
import { FaShoppingCart, FaWhatsapp, FaMapMarkerAlt, FaPhoneAlt, FaEnvelope, FaTimes } from 'react-icons/fa';
import { getAlicuotaPercent } from '../utils/tax';

// Leaflet Imports
import { MapContainer, TileLayer, Marker, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix Leaflet Icons
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';
let DefaultIcon = L.icon({
    iconUrl: icon,
    shadowUrl: iconShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41]
});
L.Marker.prototype.options.icon = DefaultIcon;

// Helper: Centrar mapa
const MapRecenter = ({ lat, lng }) => {
    const map = useMap();
    useEffect(() => {
        if (lat && lng) map.setView([lat, lng], 15);
    }, [lat, lng, map]);
    return null;
};

// Helper: Marker arrastrable
const DraggableMarker = ({ position, onDragEnd }) => {
    const markerRef = useRef(null);
    const eventHandlers = useMemo(
        () => ({
            dragend() {
                const marker = markerRef.current;
                if (marker != null) {
                    onDragEnd(marker.getLatLng());
                }
            },
        }),
        [onDragEnd],
    );

    return (
        <Marker
            draggable={true}
            eventHandlers={eventHandlers}
            position={position}
            ref={markerRef}
        />
    );
};

const PublicShop = () => {
    // ... (no changes to state/logic) ...
    const { slug } = useParams();
    const [shopData, setShopData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [cart, setCart] = useState([]);
    const [isCartOpen, setIsCartOpen] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [selectedVariant, setSelectedVariant] = useState(null);
    const [showDetails, setShowDetails] = useState(false);
    const [showLightbox, setShowLightbox] = useState(false);
    const [lightboxImage, setLightboxImage] = useState('');
    const [activeImgIdx, setActiveImgIdx] = useState(0);
    const [detailQty, setDetailQty] = useState(1);
    const [category, setCategory] = useState('Todas');
    const [checkoutStep, setCheckoutStep] = useState('cart');
    const [shippingMethod, setShippingMethod] = useState('pickup');
    const [selectedZone, _setSelectedZone] = useState(null);
    const [customerInfo, setCustomerInfo] = useState({
        name: '', phone: '', email: '',
        address: { street: '', number: '', city: '', province: '' }
    });
    const [paymentMethod, setPaymentMethod] = useState('efectivo');
    const [isProcessing, setIsProcessing] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [formError, setFormError] = useState('');
    const skipNextGeocode = useRef(false);
    const lastGeocodedStreet = useRef('');
    const lastGeocodedCity = useRef('');

    // Map State
    const [mapCenter, setMapCenter] = useState([-34.6037, -58.3816]); // Buenos Aires Default
    const [markerPos, setMarkerPos] = useState(null);
    const [isGeocoding, setIsGeocoding] = useState(false);
    const [geoError, setGeoError] = useState(null);
    const [currentBgIdx, setCurrentBgIdx] = useState(0);

    // Responsive helper
    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Carga dinámica de fuentes de Google Fonts
    const brandFont = shopData?.settings?.brandFont || 'Inter';
    useEffect(() => {
        if (!brandFont || brandFont === 'Inter') return;
        const link = document.createElement('link');
        link.href = `https://fonts.googleapis.com/css2?family=${brandFont.replace(/ /g, '+')}:wght@400;700;900&display=swap`;
        link.rel = 'stylesheet';
        document.head.appendChild(link);
        return () => {
            try { document.head.removeChild(link); } catch (e) { }
        };
    }, [brandFont]);

    // Geocoding Effect (Nominatim)
    useEffect(() => {
        const { street, number, city, province } = customerInfo.address;
        if (!street || !city) return; // Permitir que falte el número para buscar calle sola, pero no al revés

        if (skipNextGeocode.current) {
            skipNextGeocode.current = false;
            lastGeocodedStreet.current = street;
            lastGeocodedCity.current = city;
            return;
        }

        // Si ya tenemos una posición markerPos y la calle/ciudad no cambiaron, 
        // no re-geocodificar por cambios en 'number' (evita el salto molesto tras GPS)
        if (markerPos && street === lastGeocodedStreet.current && city === lastGeocodedCity.current) {
            return;
        }

        const timer = setTimeout(async () => {
            if (!number) {
                // Si no hay número, solo geocodificamos si la calle cambió significativamente
                if (street === lastGeocodedStreet.current && city === lastGeocodedCity.current) return;
            }

            setIsGeocoding(true);
            setGeoError(null);
            try {
                // Estrategia: Nominatim Estructurado (Mejor opción gratuita)
                // Priorizamos búsqueda estructurada que suele ser más precisa para alturas
                let foundData = null;


                // 2. Nominatim Estructurado (Fallback si no hay Google Key o falló)
                if (!foundData) {
                    try {
                        const structuredUrl = `https://nominatim.openstreetmap.org/search?format=json&street=${encodeURIComponent(number + ' ' + street)}&city=${encodeURIComponent(city)}&state=${encodeURIComponent(province)}&country=Argentina&limit=1`;
                        const res = await fetch(structuredUrl);
                        const data = await res.json();
                        if (data && data.length > 0) {
                            foundData = { lat: data[0].lat, lon: data[0].lon };
                        }
                    } catch (e) { console.error(e); }
                }

                // 3. Nominatim Invertido
                if (!foundData) {
                    try {
                        const structuredUrl = `https://nominatim.openstreetmap.org/search?format=json&street=${encodeURIComponent(street + ' ' + number)}&city=${encodeURIComponent(city)}&state=${encodeURIComponent(province)}&country=Argentina&limit=1`;
                        const res = await fetch(structuredUrl);
                        const data = await res.json();
                        if (data && data.length > 0) {
                            foundData = { lat: data[0].lat, lon: data[0].lon };
                        }
                    } catch (e) { console.error(e); }
                }

                // 4. Búsqueda libre (Fallback final)
                if (!foundData) {
                    try {
                        let query = `${street} ${number}, ${city}, ${province}, Argentina`;
                        let res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=1`);
                        let data = await res.json();
                        if (data && data.length > 0) {
                            foundData = { lat: data[0].lat, lon: data[0].lon };
                        }
                    } catch (e) { console.error(e); }
                }

                // 5. Aproximación (Calle sin número)
                if (!foundData && number) {
                    try {
                        let query = `${street}, ${city}, ${province}, Argentina`;
                        let res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=1`);
                        let data = await res.json();
                        if (data && data.length > 0) {
                            foundData = { lat: data[0].lat, lon: data[0].lon };
                            setGeoError("No encontramos la altura exacta. Por favor ajusta el pin manualmente.");
                        }
                    } catch (e) { console.error(e); }
                }

                const data = foundData ? [foundData] : [];

                if (data && data.length > 0) {
                    const { lat, lon } = data[0];
                    const newPos = [parseFloat(lat), parseFloat(lon)];
                    setMapCenter(newPos);
                    setMarkerPos(newPos);
                    setCustomerInfo(prev => ({
                        ...prev,
                        address: { ...prev.address, lat: newPos[0], lng: newPos[1] }
                    }));
                    lastGeocodedStreet.current = street;
                    lastGeocodedCity.current = city;
                } else {
                    setGeoError("No pudimos ubicar la dirección en el mapa.");
                }
            } catch (e) {
                console.error("Geocoding error", e);
                setGeoError("Error de conexión con el mapa.");
            } finally {
                setIsGeocoding(false);
            }
        }, 1500);
        return () => clearTimeout(timer);
    }, [customerInfo.address, markerPos]);

    // Ajustar método de envío inicial según configuración del comercio
    useEffect(() => {
        if (shopData?.settings) {
            const { pickupEnabled, deliveryEnabled } = shopData.settings;
            // Si el actual está deshabilitado, cambiar al otro
            if (shippingMethod === 'pickup' && pickupEnabled === false && deliveryEnabled !== false) {
                setShippingMethod('delivery');
            } else if (shippingMethod === 'delivery' && deliveryEnabled === false && pickupEnabled !== false) {
                setShippingMethod('pickup');
            }
        }
    }, [shopData, shippingMethod]);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Keyboard support: Close modals with Escape
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                if (showLightbox) {
                    setShowLightbox(false);
                } else if (showDetails) {
                    setShowDetails(false);
                } else if (isCartOpen) {
                    setIsCartOpen(false);
                }
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [showDetails, showLightbox, isCartOpen]);

    useEffect(() => {
        const fetchShop = async () => {
            try {
                const res = await http.get(`/public/shop/${slug}`);
                setShopData(res.data);
                // Incrementar visita
                http.post(`/public/shop/visit/${slug}`).then(res => {
                    if (res.data && res.data.visitCount !== undefined) {
                        setShopData(prev => ({...prev, visitCount: res.data.visitCount}));
                    }
                }).catch(() => {});
            } catch (err) {
                console.error('Error fetching shop:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchShop();
    }, [slug]);

    // Dinámicamente actualizar títulos y meta tags para SEO / Redes Sociales (Open Graph)
    useEffect(() => {
        if (shopData) {
            document.title = `${shopData.storeName} | Tienda Online`;
            
            // Meta Description
            let metaDesc = document.querySelector('meta[name="description"]');
            if (!metaDesc) {
                metaDesc = document.createElement('meta');
                metaDesc.setAttribute('name', 'description');
                document.head.appendChild(metaDesc);
            }
            metaDesc.setAttribute('content', `Comprá online en ${shopData.storeName}. Encontrá las mejores ofertas, productos y retiro en sucursal o envío a domicilio.`);
            
            // OpenGraph Title
            let ogTitle = document.querySelector('meta[property="og:title"]');
            if (!ogTitle) {
                ogTitle = document.createElement('meta');
                ogTitle.setAttribute('property', 'og:title');
                document.head.appendChild(ogTitle);
            }
            ogTitle.setAttribute('content', `${shopData.storeName} | Tienda Online`);

            // OpenGraph Description
            let ogDesc = document.querySelector('meta[property="og:description"]');
            if (!ogDesc) {
                ogDesc = document.createElement('meta');
                ogDesc.setAttribute('property', 'og:description');
                document.head.appendChild(ogDesc);
            }
            ogDesc.setAttribute('content', `Comprá online en ${shopData.storeName}. Encontrá las mejores ofertas, productos y envíos.`);

            // OpenGraph Image (Logo)
            if (shopData.settings?.logoUrl) {
                const shopLogoUrl = getOptimizedUrl(shopData.settings.logoUrl, 200);
                let ogImg = document.querySelector('meta[property="og:image"]');
                if (!ogImg) {
                    ogImg = document.createElement('meta');
                    ogImg.setAttribute('property', 'og:image');
                    document.head.appendChild(ogImg);
                }
                ogImg.setAttribute('content', window.location.origin + shopLogoUrl);
            }
        }
    }, [shopData]);

    const getDisplayPrice = (product, variant = null) => {
        if (!product) return 0;
        let price = product.ecommercePrice != null ? product.ecommercePrice : product.salePrice;
        if (variant) {
            if (variant.ecommercePrice != null) price = variant.ecommercePrice;
            else if (variant.price != null) price = variant.price;
        }

        // Aplicar descuento del POS si tiene discountType definido
        if (!variant && product.discountType && product.discountType !== 'none' && product.discountValue > 0) {
            if (product.discountType === 'percent') {
                price = price * (1 - product.discountValue / 100);
            } else if (product.discountType === 'fixed') {
                price = Math.max(0, price - product.discountValue);
            }
        }

        // Si el tenant es Responsable Inscripto, sumar IVA (en DB suele estar Neto)
        // condicionIVA === 1 => Responsable Inscripto
        const isRI = shopData?.fiscalConfig?.condicionIVA === 1 || shopData?.fiscalConfig?.tipoResponsable === 'IVA Responsable Inscripto';
        if (isRI) {
            const alicuota = getAlicuotaPercent(product.alicuotaIVACode || 5);
            price = price * (1 + alicuota / 100);
        }

        return price;
    };

    // Calcula info de descuento para mostrar en la tarjeta
    const getDiscountInfo = (product, variant = null) => {
        const isRI = shopData?.fiscalConfig?.condicionIVA === 1 || shopData?.fiscalConfig?.tipoResponsable === 'IVA Responsable Inscripto';
        const ivaFactor = isRI ? (1 + (getAlicuotaPercent(product.alicuotaIVACode || 5) / 100)) : 1;

        // Precio base sin descuento (siempre salePrice del POS)
        const basePrice = product.salePrice * ivaFactor;
        const finalPrice = getDisplayPrice(product, variant);

        // Hay descuento si el precio final es menor al precio base
        const hasDiscount = finalPrice < basePrice - 0.01;
        const pctOff = hasDiscount && basePrice > 0 ? Math.round(((basePrice - finalPrice) / basePrice) * 100) : 0;

        // También marcar como oferta si onSale está activo
        const isOffer = hasDiscount || product.onSale;

        return { hasDiscount, basePrice, finalPrice, pctOff, isOffer };
    };

    const addToCart = (product, variant = null, qty = 1) => {
        // Bloqueo de stock
        const stockToCheck = variant ? variant.stock : product.stock;
        if (!product.noControlStock && stockToCheck <= 0) return;

        const variantName = variant ?
            [variant.attributes?.color, variant.attributes?.size, variant.attributes?.style].filter(Boolean).join(' / ') || variant.sku
            : null;

        const addQty = Math.max(1, Math.floor(qty));

        setCart(prev => {
            // El item es el mismo si tiene el mismo ID de producto Y el mismo ID de variante
            const existing = prev.find(item =>
                item._id === product._id &&
                (variant ? item.variantId === variant._id : !item.variantId)
            );

            if (existing) {
                // Bloqueo de stock para incremento
                const newQty = existing.qty + addQty;
                if (!product.noControlStock && newQty > stockToCheck) {
                    alert(`No hay suficiente stock para ${product.name}${variantName ? ' (' + variantName + ')' : ''}. Stock disponible: ${stockToCheck}.`);
                    return prev;
                }

                return prev.map(item =>
                    (item._id === product._id && (variant ? item.variantId === variant._id : !item.variantId))
                        ? { ...item, qty: newQty }
                        : item
                );
            }

            // Verificar stock suficiente para la cantidad solicitada
            if (!product.noControlStock && addQty > stockToCheck) {
                alert(`No hay suficiente stock para ${product.name}${variantName ? ' (' + variantName + ')' : ''}. Stock disponible: ${stockToCheck}.`);
                return prev;
            }

            return [...prev, {
                ...product,
                qty: addQty,
                currentPrice: getDisplayPrice(product, variant),
                variantId: variant ? variant._id : null,
                variantName: variantName
            }];
        });
    };

    // 🌐 Image Optimization Helper
    const getOptimizedUrl = (url, width = 600) => {
        if (!url) return '';
        // Si ya es una URL optimizada o externa, no tocarla
        if (url.startsWith('http') || url.includes('/api/public/image/')) return url;
        
        // Si es una imagen local (subida por el POS)
        let filename = url;
        if (filename.startsWith('/api/uploads/')) {
            filename = filename.replace('/api/uploads/', '');
        } else if (filename.startsWith('/uploads/')) {
            filename = filename.replace('/uploads/', '');
        }
        
        return `/api/public/image/${filename}?w=${width}&q=80`;
    };

    // 📸 Prioritización de fotos: Cámara > Externas
    const getPrioritizedImageUrl = (product) => {
        if (!product) return '/placeholder.png';
        
        // Unificamos todas las fotos
        const allImg = [product.image, ...(product.ecommerceImages || [])]
            .filter(img => img && !img.includes('placeholder'));
            
        // FILTRO CRÍTICO: Solo dejamos las fotos locales (Cámara/Subidas)
        // Quitamos cualquier cosa que empiece con http:// o https:// (Externas)
        const localImages = allImg.filter(img => !img.startsWith('http'));
        
        if (localImages.length === 0) return '/placeholder.png';
        
        // La primera foto local es la portada
        return localImages[0];
    };

    const cartTotal = cart.reduce((acc, item) => acc + (item.currentPrice * item.qty), 0);
    const cartCount = cart.reduce((acc, item) => acc + item.qty, 0);

    const [orderConfirmation, setOrderConfirmation] = useState(null);

    const getWhatsAppOrderLink = () => {
        if (!orderConfirmation) return '';
        const { code, total, items, customer, shippingMethod, paymentMethod, shippingCost, shippingZoneName } = orderConfirmation;
        
        let message = `*¡Hola! Vengo de tu tienda online y acabo de realizar un pedido.*\n\n`;
        message += `*Código de Pedido:* ${code}\n`;
        message += `*Cliente:* ${customer.name}\n`;
        message += `*Teléfono:* ${customer.phone}\n`;
        message += `*Entrega:* ${shippingMethod === 'delivery' ? 'Envío a domicilio' : 'Retiro en sucursal'}\n`;
        if (shippingMethod === 'delivery' && customer.address) {
            message += `*Dirección:* ${customer.address.street} ${customer.address.number}, ${customer.address.city}, ${customer.address.province || ''}\n`;
        }
        message += `*Pago:* ${paymentMethod === 'bank_transfer' ? 'Transferencia Bancaria' : 'Efectivo / Acordar'}\n\n`;
        message += `*Productos:*\n`;
        items.forEach(item => {
            const variantStr = item.variantName ? ` (${item.variantName})` : '';
            message += `- ${item.qty}x ${item.name}${variantStr} - $${(item.qty * item.currentPrice).toLocaleString('es-AR')}\n`;
        });
        if (shippingMethod === 'delivery' && shippingCost > 0) {
            const zoneStr = shippingZoneName ? ` (${shippingZoneName})` : '';
            message += `\n*Envío${zoneStr}:* $${Number(shippingCost).toLocaleString('es-AR')}\n`;
        }
        message += `\n*Total:* $${total.toLocaleString('es-AR')}`;

        return `https://wa.me/${whatsappLink}?text=${encodeURIComponent(message)}`;
    };

    const handleGoToPayment = () => {
        setFormError('');
        if (!customerInfo.name?.trim()) {
            setFormError('Por favor completa tu nombre completo.');
            return;
        }
        if (!customerInfo.phone?.trim()) {
            setFormError('Por favor ingresa un número de teléfono de contacto para continuar.');
            return;
        }
        if (shippingMethod === 'delivery') {
            if (!customerInfo.address?.street?.trim()) {
                setFormError('Por favor ingresa la calle para el envío a domicilio.');
                return;
            }
            if (!customerInfo.address?.number?.trim()) {
                setFormError('Por favor ingresa la altura (N°) del domicilio.');
                return;
            }
        }
        setCheckoutStep('payment');
    };

    const handleCheckout = async () => {
        try {
            setIsProcessing(true);
            const payload = {
                tenantId: shopData.id || slug,
                cart,
                customer: customerInfo,
                shippingMethod,
                shippingZone: selectedZone,
                paymentMethodId: paymentMethod
            };

            const res = await http.post('/public/shop/checkout', payload);

            if (res.data.paymentUrl) {
                window.location.href = res.data.paymentUrl;
            } else if (res.data.success) {
                const shippingFee = selectedZone?.cost || 0;
                const orderData = {
                    code: res.data.orderCode,
                    orderId: res.data.orderId,
                    total: res.data.total,
                    shippingCost: shippingFee,
                    shippingZoneName: selectedZone?.name || '',
                    items: [...cart],
                    customer: { ...customerInfo },
                    shippingMethod,
                    paymentMethod
                };
                setOrderConfirmation(orderData);
                setCheckoutStep('success');
                setCart([]);

                // Redirigir a WhatsApp automáticamente si no es Mercado Pago
                if (paymentMethod !== 'mercadopago') {
                    let message = `*¡Hola! Vengo de tu tienda online y acabo de realizar un pedido.*\n\n`;
                    message += `*Código de Pedido:* ${res.data.orderCode}\n`;
                    message += `*Cliente:* ${customerInfo.name}\n`;
                    message += `*Teléfono:* ${customerInfo.phone}\n`;
                    message += `*Entrega:* ${shippingMethod === 'delivery' ? 'Envío a domicilio' : 'Retiro en sucursal'}\n`;
                    if (shippingMethod === 'delivery' && customerInfo.address) {
                        message += `*Dirección:* ${customerInfo.address.street} ${customerInfo.address.number}, ${customerInfo.address.city}, ${customerInfo.address.province || ''}\n`;
                    }
                    message += `*Pago:* ${paymentMethod === 'bank_transfer' ? 'Transferencia Bancaria' : 'Efectivo / Acordar'}\n\n`;
                    message += `*Productos:*\n`;
                    cart.forEach(item => {
                        const variantStr = item.variantName ? ` (${item.variantName})` : '';
                        message += `- ${item.qty}x ${item.name}${variantStr} - $${(item.qty * item.currentPrice).toLocaleString('es-AR')}\n`;
                    });
                    if (shippingMethod === 'delivery' && shippingFee > 0) {
                        const zoneStr = selectedZone?.name ? ` (${selectedZone.name})` : '';
                        message += `\n*Envío${zoneStr}:* $${Number(shippingFee).toLocaleString('es-AR')}\n`;
                    }
                    message += `\n*Total:* $${res.data.total.toLocaleString('es-AR')}`;

                    const waUrl = `https://wa.me/${whatsappLink}?text=${encodeURIComponent(message)}`;
                    setTimeout(() => {
                        window.open(waUrl, '_blank');
                    }, 500);
                }
            }
        } catch (err) {
            console.error('Error during checkout:', err);
            const msg = err.response?.data?.message || 'Hubo un error al procesar tu pedido.';
            const detail = err.response?.data?.detail ? `\n\nDetalle: ${err.response.data.detail}` : '';
            alert(msg + detail);
        } finally {
            setIsProcessing(false);
        }
    };



    // Reset gallery index and quantity when product changes
    useEffect(() => {
        setActiveImgIdx(0);
        setDetailQty(1);
    }, [selectedProduct]);

    // Optimized Hero Asset List
    const heroProducts = useMemo(() => {
        if (!shopData?.products?.length) return [];
        // Extract a manageable subset: 4 for Mobile, 6 for PC
        const limit = isMobile ? 4 : 6;
        const list = [];
        for (const p of shopData.products) {
            const img = getPrioritizedImageUrl(p);
            if (img && img !== '/placeholder.png') {
                list.push(getOptimizedUrl(img, isMobile ? 400 : 800));
                if (list.length >= limit) break;
            }
        }
        return list;
    }, [shopData?.products, isMobile]);

    // Background Rotation logic - Stable and performant
    useEffect(() => {
        if (heroProducts.length <= 1) return;
        const interval = setInterval(() => {
            setCurrentBgIdx(prev => (prev + 1) % heroProducts.length);
        }, 7000); // Slower rotate for PC performance
        return () => clearInterval(interval);
    }, [heroProducts.length]);

    // Smart Preloader: Preload only the FIRST and SECOND images to make the initial view ready
    useEffect(() => {
        if (heroProducts.length > 0) {
            [0, 1].forEach(idx => {
               if (heroProducts[idx]) {
                   const img = new Image();
                   img.src = heroProducts[idx];
               }
            });
        }
    }, [heroProducts]);


    if (loading) return <div style={styles.center}>Cargando tienda...</div>;
    if (!shopData) return <div style={styles.center}>Tienda no encontrada 😕</div>;

    const normalizeText = (text) => {
        if (!text) return '';
        return String(text)
            .toLowerCase()
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "");
    };

    const categories = ['Todas', ...new Set(shopData.products.map(p => p.category))];
    const filteredProducts = shopData.products.filter(p => {
        const matchesCategory = !searchTerm.trim() ? (category === 'Todas' || p.category === category) : true;

        if (!searchTerm.trim()) return matchesCategory;

        const normalizedSearch = normalizeText(searchTerm);
        const searchTokens = normalizedSearch.split(/\s+/).filter(Boolean);

        const variantNames = (p.variants || []).map(v => `${v.name || ''} ${v.sku || ''}`).join(' ');
        const searchableText = normalizeText(
            `${p.name || ''} ${p.category || ''} ${p.ecommerceDescription || ''} ${variantNames}`
        );

        const matchesSearch = searchTokens.every(token => searchableText.includes(token));

        return matchesCategory && matchesSearch;
    });

    const formatPrice = (val) => {
        if (val === null || val === undefined) return '0';
        return Number(val).toLocaleString('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
    };

    // Colores personalizados de la tienda
    const buttonColor = shopData.settings?.buttonColor || '#3483fa';
    const cartButtonColor = shopData.settings?.cartButtonColor || '#3483fa';
    const brandColor = shopData.settings?.brandColor || '#00CED1';
    const headerColor = shopData.settings?.headerColor || '#00CED1';
    const logoUrl = getOptimizedUrl(shopData.settings?.logoUrl, 200);
    const minPurchaseAmount = shopData.settings?.minPurchaseAmount || 0;

    // Normalizar WhatsApp para enlace internacional (Arg)
    const rawWs = shopData.settings?.whatsapp || '';
    const cleanWs = rawWs.replace(/\D/g, '');
    const whatsappLink = cleanWs.length === 10 ? `549${cleanWs}` : cleanWs;

    return (
        <div style={styles.container}>


            {/* Premium Hero Banner */}
            {!searchTerm && (
                <div style={{
                    width: '100%',
                    height: isMobile ? '100px' : '180px',
                    position: 'relative',
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: '#0f172a',
                }}>
                    {/* Cinematic Catalog Background (Multiple Floating Items) */}
                    <AnimatePresence mode="popLayout">
                        <motion.div
                            key={currentBgIdx}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 2, ease: "easeInOut" }}
                            style={{
                                position: 'absolute', inset: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                overflow: 'hidden'
                            }}
                        >
                              {shopData.settings?.bannerUrl ? (
                                <div style={{ 
                                    width: '100%', height: '100%',
                                    backgroundImage: `url("${shopData.settings.bannerUrl}")`,
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'center',
                                    backgroundRepeat: 'no-repeat',
                                    position: 'relative',
                                    zIndex: 1
                                }}></div>
                              ) : heroProducts.length > 0 ? (
                                <div style={{
                                    width: '100%', height: '100%',
                                    position: 'relative', overflow: 'hidden',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                                }}>
                                    {/* Blurred Background Layer for Atmosphere */}
                                    <motion.img
                                        key={`bg-${currentBgIdx}`}
                                        src={heroProducts[currentBgIdx]}
                                        initial={{ opacity: 0, scale: 1.1 }}
                                        animate={{ opacity: 0.3, scale: 1 }}
                                        transition={{ duration: 2 }}
                                        style={{ 
                                            position: 'absolute', inset: 0, width: '100%', height: '100%',
                                            objectFit: 'cover', filter: 'blur(40px)', pointerEvents: 'none'
                                        }}
                                    />

                                    {/* Main Sharp Product */}
                                    <motion.div
                                        key={`main-${currentBgIdx}`}
                                        initial={{ opacity: 0, scale: 0.8, x: -50 }}
                                        animate={{ opacity: 1, scale: 1, x: 0 }}
                                        exit={{ opacity: 0, scale: 1.1, x: 50 }}
                                        transition={{ duration: 0.8, ease: "easeOut" }}
                                        style={{ 
                                            position: 'relative', zIndex: 2,
                                            height: isMobile ? '80px' : '140px',
                                            width: isMobile ? '80px' : '140px',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center'
                                        }}
                                    >
                                        <img 
                                            src={heroProducts[currentBgIdx]} 
                                            alt="" 
                                            fetchpriority="high"
                                            style={{ 
                                                maxHeight: '100%', maxWidth: '100%', 
                                                objectFit: 'contain',
                                                filter: 'drop-shadow(0 20px 40px rgba(0,0,0,0.5))'
                                            }} 
                                        />
                                    </motion.div>
                                </div>
                              ) : (
                                <div style={{ 
                                    width: '100%', height: '100%', 
                                    background: `linear-gradient(135deg, #1e293b 0%, ${brandColor}33 100%)` 
                                }}></div>
                              )}
                        </motion.div>
                    </AnimatePresence>

                    {/* Rich Cinematic Vignette */}
                    <div style={{ 
                        position: 'absolute', inset: 0, 
                        background: `radial-gradient(circle, transparent 10%, rgba(15, 23, 42, 0.3) 60%, rgba(15, 23, 42, 0.9) 100%),
                                    linear-gradient(to bottom, transparent 40%, rgba(15, 23, 42, 0.95) 100%)`,
                        zIndex: 3
                    }}></div>

                    {/* Glassmorphism Content Box */}
                    <motion.div 
                        initial={{ y: 20, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ delay: 0.3, duration: 0.8 }}
                        style={{ 
                            position: 'relative', zIndex: 10, 
                            padding: isMobile ? '8px 15px' : '14px 30px',
                            background: 'rgba(255, 255, 255, 0.03)',
                            backdropFilter: 'blur(20px) saturate(180%)',
                            borderRadius: isMobile ? '8px' : '16px',
                            border: '1px solid rgba(255, 255, 255, 0.1)',
                            boxShadow: '0 15px 30px rgba(0, 0, 0, 0.5), inset 0 0 15px rgba(255,255,255,0.05)',
                            textAlign: 'center',
                            maxWidth: '85%'
                        }}
                    >
                        <h2 style={{
                            fontSize: isMobile ? '18px' : '34px',
                            fontWeight: '900',
                            margin: 0,
                            color: '#fff',
                            letterSpacing: '-1px',
                            lineHeight: 1.1,
                            fontFamily: brandFont
                        }}>
                            Bienvenidos a 
                            <span style={{ 
                                color: brandColor, 
                                display: 'block',
                                marginTop: '4px',
                                background: `linear-gradient(to right, ${brandColor}, #fff)`,
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                textShadow: 'none'
                            }}>{shopData.storeName}</span>
                        </h2>
                        <p style={{
                            fontSize: isMobile ? '10px' : '13px',
                            marginTop: '6px',
                            fontWeight: '400',
                            color: 'rgba(255,255,255,0.8)',
                            letterSpacing: '1.5px',
                            textTransform: 'uppercase',
                            opacity: 0.9
                        }}>
                            Las mejores ofertas en un solo lugar.
                        </p>
                    </motion.div>
                </div>
            )}

            {/* Elegant Top Bar */}
            <div style={{
                backgroundColor: '#0f172a',
                color: 'rgba(255,255,255,0.7)',
                padding: isMobile ? '8px 0' : '10px 0',
                fontSize: '13px',
                borderBottom: '1px solid rgba(255,255,255,0.05)',
                fontWeight: '500'
            }}>
                <div style={{
                    ...styles.topBarContent,
                    padding: isMobile ? '0 10px' : '0 40px',
                    flexDirection: isMobile ? 'column' : 'row',
                    gap: isMobile ? '8px' : '15px',
                    alignItems: 'center'
                }}>
                    <div style={{
                        ...styles.topBarItems,
                        justifyContent: isMobile ? 'center' : 'flex-start',
                        gap: isMobile ? '12px' : '20px',
                        flexWrap: 'wrap'
                    }}>
                        {shopData.settings?.address && (
                            <div style={{ ...styles.topBarItem, fontSize: isMobile ? '12px' : '15px', fontWeight: '700', color: '#fff', opacity: 1 }}>
                                <FaMapMarkerAlt size={isMobile ? 13 : 16} color={brandColor} />
                                <span>{shopData.settings.address}</span>
                            </div>
                        )}
                        {shopData.settings?.phone && (
                            <div style={{ ...styles.topBarItem, fontSize: isMobile ? '12px' : '15px', fontWeight: '700', color: '#fff', opacity: 1 }}>
                                <FaPhoneAlt size={isMobile ? 13 : 16} color={brandColor} />
                                <span>{shopData.settings.phone}</span>
                            </div>
                        )}
                    </div>
                    <div style={{
                        ...styles.topBarRight,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: isMobile ? 'center' : 'flex-end',
                        flexWrap: 'wrap',
                        gap: isMobile ? '8px' : '15px'
                    }}>
                        {!isMobile && (
                            <span style={{ color: brandColor, fontWeight: '700' }}>✨ ¡Compra 100% segura por WhatsApp! ✨</span>
                        )}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'rgba(255,255,255,0.15)', padding: isMobile ? '3px 8px' : '5px 12px', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.1)' }}>
                            {/* Mercado Pago SVG */}
                            <svg width={isMobile ? "14" : "17"} height={isMobile ? "14" : "17"} viewBox="0 0 24 24" fill="#009EE3" style={{ verticalAlign: 'middle' }}>
                                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 15h-2v-6h2v6zm0-8h-2V7h2v2z" />
                            </svg>
                            {/* Card SVG */}
                            <svg width={isMobile ? "14" : "17"} height={isMobile ? "14" : "17"} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: 'middle' }}>
                                <rect x="2" y="5" width="20" height="14" rx="2" />
                                <line x1="2" y1="10" x2="22" y2="10" />
                            </svg>
                            {/* Bank SVG */}
                            <svg width={isMobile ? "14" : "17"} height={isMobile ? "14" : "17"} viewBox="0 0 24 24" fill="none" stroke="#00b981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: 'middle' }}>
                                <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
                                <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
                            </svg>
                            <span style={{ fontSize: isMobile ? '11px' : '13px', color: '#fff', fontWeight: 'bold', letterSpacing: '0.3px' }}>Tarjetas &amp; Transferencia</span>
                        </div>
                        {/* Andreani Shipping */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'rgba(255,255,255,0.15)', padding: isMobile ? '3px 8px' : '5px 12px', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.1)' }}>
                            <svg width={isMobile ? "14" : "17"} height={isMobile ? "14" : "17"} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: 'middle' }}>
                                <rect x="1" y="3" width="15" height="13" rx="1" ry="1"></rect>
                                <polygon points="16 8 20 8 23 11 23 16 16 16 16 8"></polygon>
                                <circle cx="5.5" cy="18.5" r="2.5"></circle>
                                <circle cx="18.5" cy="18.5" r="2.5"></circle>
                            </svg>
                            <span style={{ fontSize: isMobile ? '11px' : '13px', color: '#ff6b6b', fontWeight: '900', fontStyle: 'italic', letterSpacing: '0.8px' }}>ANDREANI</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Meli-style Header */}
            <header style={{
                ...styles.meliHeader,
                backgroundColor: headerColor,
                height: isMobile ? 'auto' : '100px',
                padding: isMobile ? '10px 0' : '0',
                display: 'flex',
                alignItems: 'center'
            }}>
                <div style={{ ...styles.headerLimit, width: '100%' }}>
                    <div style={{ ...styles.headerTop, flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? '10px' : '20px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: isMobile ? '100%' : 'auto' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                {logoUrl && (
                                    <img src={logoUrl} alt={shopData.storeName} style={{
                                        height: isMobile ? '40px' : '55px',
                                        width: isMobile ? '40px' : '55px',
                                        objectFit: 'contain',
                                        borderRadius: '50%',
                                        background: '#fff',
                                        padding: '2px',
                                        border: '1px solid rgba(0,0,0,0.1)'
                                    }} />
                                )}
                                <h1 style={{ ...styles.meliLogo, margin: 0, color: brandColor, fontFamily: brandFont }}>{shopData.storeName}</h1>
                            </div>

                            {isMobile && (
                                <motion.div
                                    whileTap={{ scale: 0.9 }}
                                    animate={cartCount > 0 ? {
                                        scale: [1, 1.1, 1],
                                        boxShadow: [
                                            '0 0 0 0px rgba(52, 131, 250, 0)',
                                            '0 0 0 10px rgba(52, 131, 250, 0.4)',
                                            '0 0 0 0px rgba(52, 131, 250, 0)'
                                        ]
                                    } : {}}
                                    transition={{ repeat: Infinity, duration: 2 }}
                                    style={{
                                        ...styles.cartBtn,
                                        background: cartCount > 0 ? cartButtonColor : 'transparent',
                                        color: cartCount > 0 ? '#fff' : '#333',
                                        padding: cartCount > 0 ? '8px 12px' : '0',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        minWidth: '40px',
                                        minHeight: '40px'
                                    }}
                                    onClick={() => setIsCartOpen(true)}
                                >
                                    <FaShoppingCart />
                                    {cartCount > 0 && <span style={styles.badge}>{cartCount}</span>}
                                </motion.div>
                            )}
                        </div>
                        <div style={{ ...styles.searchContainer, width: '100%', position: 'relative' }}>
                            <input
                                type="text"
                                placeholder="Buscar productos..."
                                style={{
                                    ...styles.meliSearch,
                                    paddingRight: searchTerm ? '45px' : '25px'
                                }}
                                value={searchTerm}
                                onChange={e => {
                                    const val = e.target.value;
                                    setSearchTerm(val);
                                    if (val.trim() && category !== 'Todas') {
                                        setCategory('Todas');
                                    }
                                }}
                            />
                            {searchTerm && (
                                <button
                                    type="button"
                                    onClick={() => setSearchTerm('')}
                                    style={{
                                        position: 'absolute',
                                        right: '15px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        background: 'rgba(0,0,0,0.08)',
                                        border: 'none',
                                        borderRadius: '50%',
                                        width: '24px',
                                        height: '24px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: 'pointer',
                                        color: '#666',
                                        fontSize: '11px',
                                        padding: 0,
                                        transition: 'all 0.2s ease'
                                    }}
                                    title="Borrar búsqueda"
                                >
                                    <FaTimes />
                                </button>
                            )}
                        </div>
                        {!isMobile && (
                            <motion.div
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                animate={cartCount > 0 ? {
                                    y: [0, -4, 0]
                                } : {}}
                                transition={{ repeat: Infinity, duration: 3, ease: "easeInOut" }}
                                style={{
                                    ...styles.cartBtn,
                                    background: cartButtonColor,
                                    color: '#fff',
                                    padding: '8px 20px',
                                    borderRadius: '99px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px',
                                    fontWeight: '700',
                                    fontSize: '15px',
                                    boxShadow: cartCount > 0 ? '0 4px 12px rgba(0,0,0,0.1)' : 'none',
                                    border: '1px solid rgba(0,0,0,0.05)'
                                }}
                                onClick={() => setIsCartOpen(true)}
                            >
                                <span style={{ fontSize: '18px' }}><FaShoppingCart /></span>
                                <span>{cartCount > 0 ? `Ver Carrito ($${formatPrice(cartTotal)})` : 'Carrito vacío'}</span>
                                {cartCount > 0 && (
                                    <span style={{
                                        background: '#fff',
                                        color: cartButtonColor,
                                        fontSize: '12px',
                                        borderRadius: '10px',
                                        padding: '2px 8px',
                                        marginLeft: '4px',
                                        fontWeight: 'bold'
                                    }}>
                                        {cartCount}
                                    </span>
                                )}
                            </motion.div>
                        )}
                    </div>
                </div>
            </header>


            <main style={styles.mainContent}>
                <div style={isMobile ? {} : styles.shopLayout}>
                    {!isMobile && (
                        <aside style={styles.sidebar}>
                            <h3 style={styles.sidebarTitle}>Categorías</h3>
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                {categories.map(c => (
                                    <button
                                        key={c}
                                        style={{
                                            ...styles.sidebarLink,
                                            ...(category === c ? styles.sidebarLinkActive : {})
                                        }}
                                        onClick={() => {
                                            setCategory(c);
                                            setSearchTerm('');
                                        }}
                                    >
                                        {c}
                                        {category === c && <span style={{ marginLeft: 'auto' }}>›</span>}
                                    </button>
                                ))}
                            </div>
                        </aside>
                    )}

                    <div style={styles.contentArea}>
                        {/* Categories Scrollable (Solo Mobile) */}
                        {isMobile && (
                            <div style={styles.filterBar}>
                                {categories.map(c => (
                                    <button
                                        key={c}
                                        style={{ ...styles.filterBtn, ...(category === c ? styles.filterBtnActive : {}) }}
                                        onClick={() => {
                                            setCategory(c);
                                            setSearchTerm('');
                                        }}
                                    >
                                        {c}
                                    </button>
                                ))}
                            </div>
                        )}



                        {/* Promos / Offers Carousel - Inserted between rows and highlighted */}
                        {shopData.products.some(p => p.onSale) && (
                            <div style={{
                                margin: '30px 0',
                                background: '#fff',
                                padding: isMobile ? '20px 15px' : '30px',
                                borderRadius: '8px',
                                boxShadow: '0 4px 15px rgba(0,0,0,0.05)',
                                position: 'relative',
                                overflow: 'hidden',
                                borderTop: '4px solid #3483fa'
                            }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', position: 'relative', zIndex: 1 }}>
                                    <div>
                                        <h3 style={{ fontSize: isMobile ? '20px' : '24px', fontWeight: '800', color: '#333', margin: 0 }}>
                                            Ofertas Flash 🔥
                                        </h3>
                                        <p style={{ color: '#666', fontSize: isMobile ? '12px' : '14px', marginTop: '4px' }}>¡Solo por tiempo limitado!</p>
                                    </div>
                                    {!isMobile && (
                                        <div style={{
                                            backgroundColor: '#f5f5f5', color: '#3483fa', padding: '6px 14px',
                                            borderRadius: '20px', fontSize: '13px', fontWeight: '700', cursor: 'pointer',
                                            border: '1px solid #eee'
                                        }}>
                                            Ver todo
                                        </div>
                                    )}
                                </div>

                                <div style={{
                                    display: 'flex',
                                    overflowX: 'auto',
                                    gap: '15px',
                                    paddingBottom: '10px',
                                    position: 'relative',
                                    zIndex: 1
                                }} className="hide-scrollbar">
                                    {shopData.products.filter(p => p.onSale).map(p => {
                                        const originalPrice = p.salePrice;
                                        const currentPrice = getDisplayPrice(p);
                                        const percentOff = originalPrice > currentPrice ? Math.round(((originalPrice - currentPrice) / originalPrice) * 100) : 0;

                                        return (
                                            <motion.div
                                                key={`promo-${p._id}`}
                                                whileHover={{ scale: 1.03, boxShadow: '0 8px 15px rgba(0,0,0,0.1)' }}
                                                onClick={() => { setSelectedProduct(p); setSelectedVariant(null); setShowDetails(true); }}
                                                style={{
                                                    minWidth: isMobile ? '160px' : '200px',
                                                    backgroundColor: '#fff',
                                                    borderRadius: '8px',
                                                    overflow: 'hidden',
                                                    cursor: 'pointer',
                                                    border: '1px solid #f0f0f0',
                                                    display: 'flex',
                                                    flexDirection: 'column'
                                                }}
                                            >
                                                <div style={{ height: isMobile ? '130px' : '170px', position: 'relative', background: '#fff', padding: '10px' }}>
                                                    <img 
                                                        src={getOptimizedUrl(p.image || p.ecommerceImages?.[0] || '/placeholder.png', 400)} 
                                                        alt="" 
                                                        style={{ width: '100%', height: '100%', objectFit: 'contain' }} 
                                                        loading="lazy"
                                                        onError={(e) => {
                                                            if (e.target.src !== p.image && p.image) {
                                                                e.target.src = p.image;
                                                            } else if (e.target.src !== 'https://via.placeholder.com/300?text=Sin+Imagen') {
                                                                e.target.src = 'https://via.placeholder.com/300?text=Sin+Imagen';
                                                            }
                                                        }}
                                                    />
                                                </div>
                                                <div style={{ padding: '12px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                                                    <div style={{ background: '#ff7733', color: '#fff', alignSelf: 'flex-start', padding: '1px 5px', borderRadius: '3px', fontSize: '10px', fontWeight: '800', marginBottom: '8px' }}>MÁS VENDIDO</div>
                                                    <div style={{
                                                        fontSize: '15px',
                                                        fontWeight: '500',
                                                        color: '#333',
                                                        marginBottom: '8px',
                                                        height: '40px',
                                                        overflow: 'hidden',
                                                        display: '-webkit-box',
                                                        WebkitLineClamp: 2,
                                                        WebkitBoxOrient: 'vertical',
                                                        lineHeight: '1.3'
                                                    }}>{p.name}</div>

                                                    {originalPrice > currentPrice && (
                                                        <div style={{ fontSize: '12px', color: '#999', textDecoration: 'line-through' }}>
                                                            $ {formatPrice(originalPrice)}
                                                        </div>
                                                    )}

                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                        <span style={{ fontWeight: '700', fontSize: '22px', color: '#333' }}>$ {formatPrice(currentPrice)}</span>
                                                        {percentOff > 0 && (
                                                            <span style={{ color: '#00a650', fontSize: '14px', fontWeight: '500' }}>{percentOff}% OFF</span>
                                                        )}
                                                    </div>
                                                </div>
                                            </motion.div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* Section Header / Category Title */}
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '20px',
                            paddingBottom: '10px',
                            borderBottom: '2px solid #f1f5f9'
                        }}>
                            <div>
                                <h2 style={{
                                    fontSize: isMobile ? '20px' : '26px',
                                    fontWeight: '800',
                                    color: '#0f172a',
                                    margin: 0
                                }}>
                                    {searchTerm ? `Resultados para "${searchTerm}"` : (category === 'Todas' ? 'Todos los Productos' : category)}
                                </h2>
                                <span style={{
                                    fontSize: '13px',
                                    color: '#64748b',
                                    fontWeight: '500',
                                    marginTop: '4px',
                                    display: 'block'
                                }}>
                                    {filteredProducts.length} {filteredProducts.length === 1 ? 'producto encontrado' : 'productos encontrados'}
                                </span>
                            </div>
                        </div>

                        {/* Product Grid - Todas las filas combinadas */}
                        <div style={{
                            ...styles.productGrid,
                            gridTemplateColumns: isMobile ? 'repeat(auto-fit, minmax(150px, 1fr))' : 'repeat(auto-fit, minmax(200px, 1fr))',
                            gap: isMobile ? '10px' : '20px'
                        }}>
                            {filteredProducts.map(p => (
                                <motion.div
                                    key={`rest-${p._id}`}
                                    style={styles.meliCard}
                                    whileHover={{ boxShadow: '0 10px 20px rgba(0,0,0,0.1)' }}
                                    onClick={() => { setSelectedProduct(p); setSelectedVariant(null); setShowDetails(true); }}
                                >
                                    <div style={{ ...styles.imgContainer, height: isMobile ? '160px' : '220px' }}>
                                        <img
                                            src={getOptimizedUrl(getPrioritizedImageUrl(p), 400)}
                                            alt={p.name}
                                            style={styles.img}
                                            loading="lazy"
                                            onError={(e) => {
                                                if (e.target.src !== p.image && p.image) {
                                                    e.target.src = p.image;
                                                } else if (e.target.src !== 'https://via.placeholder.com/300?text=Sin+Imagen') {
                                                    e.target.src = 'https://via.placeholder.com/300?text=Sin+Imagen';
                                                }
                                            }}
                                        />
                                    </div>
                                    <div style={styles.cardBody}>
                                        {(() => {
                                            const di = getDiscountInfo(p);
                                            return (
                                                <>
                                                    {/* Badge OFERTA / % OFF */}
                                                    {di.isOffer ? (
                                                        <div style={{
                                                            display: 'flex', gap: '4px', alignItems: 'center',
                                                            marginBottom: '6px', flexWrap: 'wrap'
                                                        }}>
                                                            <span style={{
                                                                background: '#e53935',
                                                                color: '#fff',
                                                                padding: '2px 7px',
                                                                borderRadius: '4px',
                                                                fontSize: '10px',
                                                                fontWeight: '800',
                                                                letterSpacing: '0.5px',
                                                                textTransform: 'uppercase'
                                                            }}>🏷️ OFERTA</span>
                                                            {di.pctOff > 0 && (
                                                                <span style={{
                                                                    background: '#00a650',
                                                                    color: '#fff',
                                                                    padding: '2px 7px',
                                                                    borderRadius: '4px',
                                                                    fontSize: '10px',
                                                                    fontWeight: '800'
                                                                }}>{di.pctOff}% OFF</span>
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <div style={{ height: '22px' }} />
                                                    )}

                                                    <h3 style={styles.prodName}>{p.name}</h3>

                                                    {/* Precio original tachado */}
                                                    {di.hasDiscount && (
                                                        <div style={{
                                                            fontSize: '12px',
                                                            color: '#999',
                                                            textDecoration: 'line-through',
                                                            marginBottom: '2px'
                                                        }}>
                                                            $ {formatPrice(di.basePrice)}
                                                        </div>
                                                    )}

                                                    {/* Precio final */}
                                                    <div style={styles.meliPrice}>
                                                        <span style={di.hasDiscount ? { color: '#e53935', fontWeight: '800' } : {}}>
                                                            $ {formatPrice(di.finalPrice)}
                                                        </span>
                                                        {di.hasDiscount && (
                                                            <span style={{ color: '#00a650', fontSize: '13px', fontWeight: '600' }}>
                                                                {di.pctOff}% OFF
                                                            </span>
                                                        )}
                                                    </div>
                                                </>
                                            );
                                        })()}


                                        {!isMobile && (
                                            <motion.button
                                                whileHover={{ scale: 1.02 }}
                                                whileTap={{ scale: 0.95, backgroundColor: '#2b6fd2' }}
                                                style={{
                                                    ...styles.meliAddBtn,
                                                    background: (!p.noControlStock && p.stock <= 0) ? '#ccc' : buttonColor,
                                                    cursor: (!p.noControlStock && p.stock <= 0) ? 'not-allowed' : 'pointer'
                                                }}
                                                disabled={!p.noControlStock && p.stock <= 0}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    if (p.variants && p.variants.length > 0) {
                                                        setSelectedProduct(p);
                                                        setSelectedVariant(null);
                                                        setShowDetails(true);
                                                    } else {
                                                        addToCart(p);
                                                    }
                                                }}
                                            >
                                                {(!p.noControlStock && p.stock <= 0) ? 'Sin stock' : 'Agregar al carrito'}
                                            </motion.button>
                                        )}
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                        {filteredProducts.length === 0 && (
                            <div style={{ textAlign: 'center', padding: '50px' }}>
                                <p style={{ color: '#666' }}>No se encontraron productos que coincidan con tu búsqueda.</p>
                            </div>
                        )}
                    </div>
                </div>
            </main>

            {/* Cart Sidebar */}
            <AnimatePresence>
                {isCartOpen && (
                    <>
                        <motion.div
                            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                            style={styles.overlay} onClick={() => setIsCartOpen(false)}
                        />
                        <motion.div
                            initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
                            style={{ ...styles.cartSidebar, width: isMobile ? '100%' : '400px' }}
                        >
                            <div style={styles.cartHeader}>
                                <h2>Tu Carrito</h2>
                                <button style={styles.closeBtn} onClick={() => setIsCartOpen(false)}>✕</button>
                            </div>

                            <div style={styles.cartContent}>
                                {checkoutStep === 'cart' && (
                                    <div key="cart-list">
                                        {cart.length === 0 ? (
                                            <div style={{ textAlign: 'center', marginTop: 40 }}>
                                                <FaShoppingCart style={{ fontSize: '40px', color: '#ccc', marginBottom: '15px' }} />
                                                <p>Tu carrito está vacío.</p>
                                            </div>
                                        ) : (
                                            cart.map(item => (
                                                <div key={item._id + (item.variantId || '')} style={styles.cartItem}>
                                                    <div style={{ flex: 1 }}>
                                                        <div style={{ fontWeight: '600' }}>{item.name}</div>
                                                        {item.variantName && (
                                                            <div style={{ fontSize: '12px', color: '#3483fa', fontWeight: '500' }}>{item.variantName}</div>
                                                        )}
                                                        <div style={{ fontSize: 13, color: '#666' }}>{item.qty} x ${item.currentPrice.toLocaleString()}</div>
                                                    </div>
                                                    <div style={{ fontWeight: 'bold' }}>${(item.qty * item.currentPrice).toLocaleString()}</div>
                                                    <button
                                                        onClick={() => setCart(cart.filter(i => !(i._id === item._id && i.variantId === item.variantId)))}
                                                        style={{ marginLeft: '10px', background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer' }}
                                                    >✕</button>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                )}

                                {checkoutStep === 'shipping' && (
                                    <div style={styles.checkoutSection} key="shipping-select">
                                        <h3>Método de Entrega</h3>
                                        <div style={styles.optionGroup}>
                                            {(shopData.settings?.pickupEnabled !== false) && (
                                                <div
                                                    style={{ ...styles.optionCard, ...(shippingMethod === 'pickup' ? styles.optionCardActive : {}) }}
                                                    onClick={() => setShippingMethod('pickup')}
                                                >
                                                    <div style={{ fontWeight: 'bold' }}>Retiro en sucursal</div>
                                                    <div style={{ fontSize: '12px' }}>Gratis</div>
                                                </div>
                                            )}
                                            {(shopData.settings?.deliveryEnabled !== false) && (
                                                <div
                                                    style={{ ...styles.optionCard, ...(shippingMethod === 'delivery' ? styles.optionCardActive : {}) }}
                                                    onClick={() => setShippingMethod('delivery')}
                                                >
                                                    <div style={{ fontWeight: 'bold' }}>Envío a domicilio</div>
                                                    <div style={{ fontSize: '12px' }}>A coordinar</div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {checkoutStep === 'customer' && (
                                    <div style={styles.checkoutSection} key="customer-data">
                                        <h3>Tus Datos</h3>
                                        {formError && (
                                            <motion.div
                                                initial={{ opacity: 0, y: -10 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                style={{
                                                    backgroundColor: '#fef2f2',
                                                    border: '1px solid #fca5a5',
                                                    color: '#991b1b',
                                                    padding: '10px 14px',
                                                    borderRadius: '8px',
                                                    marginBottom: '15px',
                                                    fontSize: '13px',
                                                    fontWeight: '600',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '8px'
                                                }}
                                            >
                                                <span>⚠️</span>
                                                <span>{formError}</span>
                                            </motion.div>
                                        )}
                                        <div style={{ marginBottom: '10px' }}>
                                            <input
                                                type="text" placeholder="Nombre completo *" style={{
                                                    ...styles.input,
                                                    marginBottom: '2px',
                                                    borderColor: formError && !customerInfo.name?.trim() ? '#ef4444' : '#ddd',
                                                    backgroundColor: formError && !customerInfo.name?.trim() ? '#fff5f5' : '#fff'
                                                }}
                                                value={customerInfo.name} onChange={e => {
                                                    setFormError('');
                                                    setCustomerInfo(p => ({ ...p, name: e.target.value }));
                                                }}
                                            />
                                            {formError && !customerInfo.name?.trim() && (
                                                <span style={{ color: '#ef4444', fontSize: '11px', fontWeight: 'bold' }}>* Ingresa tu nombre completo</span>
                                            )}
                                        </div>
                                        <div style={{ marginBottom: '10px' }}>
                                            <input
                                                type="text" placeholder="Teléfono de contacto *" style={{
                                                    ...styles.input,
                                                    marginBottom: '2px',
                                                    borderColor: formError && !customerInfo.phone?.trim() ? '#ef4444' : '#ddd',
                                                    backgroundColor: formError && !customerInfo.phone?.trim() ? '#fff5f5' : '#fff'
                                                }}
                                                value={customerInfo.phone} onChange={e => {
                                                    setFormError('');
                                                    setCustomerInfo(p => ({ ...p, phone: e.target.value }));
                                                }}
                                            />
                                            {formError && !customerInfo.phone?.trim() && (
                                                <span style={{ color: '#ef4444', fontSize: '11px', fontWeight: 'bold' }}>* Ingresa tu número de teléfono</span>
                                            )}
                                        </div>
                                        <input
                                            type="email" placeholder="Email (opcional)" style={styles.input}
                                            value={customerInfo.email} onChange={e => setCustomerInfo(p => ({ ...p, email: e.target.value }))}
                                        />
                                        {shippingMethod === 'delivery' && (
                                            <>
                                                <button
                                                    onClick={() => {
                                                        if (navigator.geolocation) {
                                                            setIsGeocoding(true);
                                                            navigator.geolocation.getCurrentPosition(
                                                                async (position) => {
                                                                    const { latitude, longitude } = position.coords;
                                                                    setMapCenter([latitude, longitude]);
                                                                    setMarkerPos([latitude, longitude]);
                                                                    setCustomerInfo(prev => ({
                                                                        ...prev,
                                                                        address: { ...prev.address, lat: latitude, lng: longitude }
                                                                    }));

                                                                    // Reverse Geocoding opcional para llenar campos
                                                                    try {
                                                                        const res = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`);
                                                                        const data = await res.json();
                                                                        if (data && data.address) {
                                                                            // A veces el número está en house_number, otras hay que extraerlo del display_name
                                                                            let houseNum = data.address.house_number || '';
                                                                            if (!houseNum && data.display_name) {
                                                                                const match = data.display_name.match(/(\d+)/);
                                                                                if (match) houseNum = match[1];
                                                                            }

                                                                            skipNextGeocode.current = true;

                                                                            // Actualizar refs para evitar que el useEffect dispare una nueva búsqueda
                                                                            const newStreet = data.address.road || '';
                                                                            const newCity = data.address.city || data.address.town || data.address.village || '';

                                                                            lastGeocodedStreet.current = newStreet;
                                                                            lastGeocodedCity.current = newCity;

                                                                            setCustomerInfo(prev => ({
                                                                                ...prev,
                                                                                address: {
                                                                                    ...prev.address,
                                                                                    street: newStreet,
                                                                                    number: houseNum,
                                                                                    city: newCity,
                                                                                    lat: latitude,
                                                                                    lng: longitude
                                                                                }
                                                                            }));
                                                                        }
                                                                    } catch (e) {
                                                                        console.error("Reverse geo error", e);
                                                                    }
                                                                    setIsGeocoding(false);
                                                                },
                                                                (error) => {
                                                                    console.error(error);
                                                                    setIsGeocoding(false);
                                                                    let msg = 'No se pudo obtener la ubicación.';
                                                                    if (error.code === 1) msg = 'Permiso denegado. Habilita la ubicación en el navegador (candado en la barra de URL).';
                                                                    else if (error.code === 2) msg = 'Ubicación no disponible (es posible que tu PC no tenga GPS/WiFi detectables).';
                                                                    else if (error.code === 3) msg = 'Se agotó el tiempo de espera.';
                                                                    else if (!window.isSecureContext) msg = 'Por seguridad, la ubicación requiere HTTPS o Localhost.';

                                                                    alert(msg + '\n\nPor favor ingresa la dirección manualmente.');
                                                                },
                                                                { enableHighAccuracy: true, timeout: 10000 }
                                                            );
                                                        } else {
                                                            alert('Tu navegador no soporta geolocalización.');
                                                        }
                                                    }}
                                                    style={{
                                                        width: '100%', padding: '10px', marginBottom: '15px',
                                                        background: '#fff', border: '1px solid #3483fa', borderRadius: '6px',
                                                        color: '#3483fa', fontWeight: '600', cursor: 'pointer',
                                                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px'
                                                    }}
                                                >
                                                    📍 Usar mi ubicación actual
                                                </button>
                                                <div style={{ display: 'flex', gap: '5px' }}>
                                                    <input
                                                        type="text" placeholder="Calle *" style={{
                                                            ...styles.input, flex: 2,
                                                            borderColor: formError && !customerInfo.address.street?.trim() ? '#ef4444' : '#ddd',
                                                            backgroundColor: formError && !customerInfo.address.street?.trim() ? '#fff5f5' : '#fff'
                                                        }}
                                                        value={customerInfo.address.street} onChange={e => {
                                                            setFormError('');
                                                            setCustomerInfo(p => ({ ...p, address: { ...p.address, street: e.target.value } }));
                                                        }}
                                                    />
                                                    <input
                                                        type="text" placeholder="N° *" style={{
                                                            ...styles.input, flex: 1,
                                                            borderColor: formError && !customerInfo.address.number?.trim() ? '#ef4444' : '#ddd',
                                                            backgroundColor: formError && !customerInfo.address.number?.trim() ? '#fff5f5' : '#fff'
                                                        }}
                                                        value={customerInfo.address.number} onChange={e => {
                                                            setFormError('');
                                                            setCustomerInfo(p => ({ ...p, address: { ...p.address, number: e.target.value } }));
                                                        }}
                                                    />
                                                </div>
                                                {formError && (!customerInfo.address.street?.trim() || !customerInfo.address.number?.trim()) && (
                                                    <span style={{ color: '#ef4444', fontSize: '11px', fontWeight: 'bold', display: 'block', marginBottom: '10px' }}>* Ingresa calle y número</span>
                                                )}
                                                <div style={{ display: 'flex', gap: '5px', marginTop: '5px' }}>
                                                    <input
                                                        type="text" placeholder="Ciudad / Barrio" style={{ ...styles.input, flex: 1 }}
                                                        value={customerInfo.address.city} onChange={e => setCustomerInfo(p => ({ ...p, address: { ...p.address, city: e.target.value } }))}
                                                    />
                                                    <select
                                                        style={{ ...styles.input, flex: 1 }}
                                                        value={customerInfo.address.province}
                                                        onChange={e => setCustomerInfo(p => ({ ...p, address: { ...p.address, province: e.target.value } }))}
                                                    >
                                                        <option value="">Provincia</option>
                                                        <option value="Buenos Aires">Buenos Aires</option>
                                                        <option value="Capital Federal">Capital Federal</option>
                                                        <option value="Catamarca">Catamarca</option>
                                                        <option value="Chaco">Chaco</option>
                                                        <option value="Chubut">Chubut</option>
                                                        <option value="Córdoba">Córdoba</option>
                                                        <option value="Corrientes">Corrientes</option>
                                                        <option value="Entre Ríos">Entre Ríos</option>
                                                        <option value="Formosa">Formosa</option>
                                                        <option value="Jujuy">Jujuy</option>
                                                        <option value="La Pampa">La Pampa</option>
                                                        <option value="La Rioja">La Rioja</option>
                                                        <option value="Mendoza">Mendoza</option>
                                                        <option value="Misiones">Misiones</option>
                                                        <option value="Neuquén">Neuquén</option>
                                                        <option value="Río Negro">Río Negro</option>
                                                        <option value="Salta">Salta</option>
                                                        <option value="San Juan">San Juan</option>
                                                        <option value="San Luis">San Luis</option>
                                                        <option value="Santa Cruz">Santa Cruz</option>
                                                        <option value="Santa Fe">Santa Fe</option>
                                                        <option value="Santiago del Estero">Santiago del Estero</option>
                                                        <option value="Tierra del Fuego">Tierra del Fuego</option>
                                                        <option value="Tucumán">Tucumán</option>
                                                    </select>
                                                </div>

                                                {/* Map Preview */}
                                                {(customerInfo.address.street || markerPos) && (
                                                    <div style={{ ...styles.mapPreviewContainer, height: '300px', padding: 0, overflow: 'hidden', position: 'relative' }}>
                                                        {isGeocoding ? (
                                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#666' }}>
                                                                <span>⏳ Buscando ubicación...</span>
                                                            </div>
                                                        ) : markerPos ? (
                                                            <>
                                                                <MapContainer center={mapCenter} zoom={18} style={{ height: '100%', width: '100%' }}>
                                                                    <TileLayer
                                                                        url="https://mt1.google.com/vt/lyrs=m&x={x}&y={y}&z={z}"
                                                                        attribution='&copy; Google Maps'
                                                                    />
                                                                    <DraggableMarker
                                                                        position={markerPos}
                                                                        onDragEnd={(newLatLng) => {
                                                                            setMarkerPos([newLatLng.lat, newLatLng.lng]);
                                                                            setCustomerInfo(prev => ({
                                                                                ...prev,
                                                                                address: { ...prev.address, lat: newLatLng.lat, lng: newLatLng.lng }
                                                                            }));
                                                                        }}
                                                                    />
                                                                    <MapRecenter lat={mapCenter[0]} lng={mapCenter[1]} />
                                                                </MapContainer>
                                                                <div style={{
                                                                    position: 'absolute', bottom: 10, left: 10, right: 10, zIndex: 999,
                                                                    background: 'rgba(255,255,255,0.95)', padding: '8px', borderRadius: '6px',
                                                                    fontSize: '12px', textAlign: 'center', boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
                                                                    border: '1px solid #ddd'
                                                                }}>
                                                                    {geoError ? (
                                                                        <span style={{ color: '#d32f2f', fontWeight: 'bold' }}>⚠️ {geoError}</span>
                                                                    ) : (
                                                                        <span style={{ color: '#d32f2f', fontWeight: 'bold' }}>📍 Verifica que el pin esté en tu puerta. Si no, arrástralo.</span>
                                                                    )}
                                                                </div>
                                                            </>
                                                        ) : (
                                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#999', fontSize: '13px', padding: '20px', textAlign: 'center' }}>
                                                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
                                                                    <span>📍</span>
                                                                    <span>{geoError ? <span style={{ color: '#d32f2f' }}>{geoError}</span> : "Complete la dirección para ubicar en el mapa..."}</span>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>
                                )}

                                {checkoutStep === 'payment' && (
                                    <div style={styles.checkoutSection} key="payment-method">
                                        <h3>Método de Pago</h3>
                                        <div style={{ ...styles.optionGroup, flexDirection: isMobile ? 'column' : 'row' }}>
                                            <div
                                                style={{ ...styles.optionCard, ...(paymentMethod === 'efectivo' ? styles.optionCardActive : {}) }}
                                                onClick={() => setPaymentMethod('efectivo')}
                                            >
                                                <div style={{ fontWeight: 'bold' }}>Efectivo / Acordar</div>
                                                <div style={{ fontSize: '12px' }}>Pago al recibir o retirar</div>
                                            </div>

                                            <div
                                                style={{ ...styles.optionCard, ...(paymentMethod === 'bank_transfer' ? styles.optionCardActive : {}) }}
                                                onClick={() => setPaymentMethod('bank_transfer')}
                                            >
                                                <div style={{ fontWeight: 'bold' }}>Transferencia</div>
                                                <div style={{ fontSize: '11px' }}>CBU / Alias bancario</div>
                                            </div>

                                            {shopData.settings?.mercadoPago?.enabled && (
                                                <div
                                                    style={{ ...styles.optionCard, ...(paymentMethod === 'mercadopago' ? styles.optionCardActive : {}) }}
                                                    onClick={() => setPaymentMethod('mercadopago')}
                                                >
                                                    <div style={{ fontWeight: 'bold' }}>Mercado Pago</div>
                                                    <div style={{ fontSize: '11px' }}>Tarjetas y Saldo MP</div>
                                                </div>
                                            )}
                                        </div>

                                        {paymentMethod === 'bank_transfer' && (
                                            <div style={{ marginTop: '15px', padding: '15px', backgroundColor: '#f3f7ff', borderRadius: '8px', border: `1px solid ${buttonColor}` }}>
                                                <p style={{ margin: 0, fontSize: '13px', color: '#333' }}>
                                                    <strong>Instrucciones:</strong> Al finalizar el pedido, te enviaremos los datos de transferencia por WhatsApp para que puedas realizar el pago y enviarnos el comprobante.
                                                </p>
                                            </div>
                                        )}
                                        {paymentMethod === 'efectivo' && (
                                            <div style={{ marginTop: '15px', padding: '15px', backgroundColor: '#f3f7ff', borderRadius: '8px', border: `1px solid ${buttonColor}` }}>
                                                <p style={{ margin: 0, fontSize: '13px', color: '#333' }}>
                                                    <strong>Instrucciones:</strong> El comercio recibirá tu pedido y se pondrá en contacto para coordinar la entrega y el pago en efectivo.
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {checkoutStep === 'success' && (
                                     <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                                         <div style={{ fontSize: '60px', marginBottom: '20px' }}>🙌</div>
                                         <h3 style={{ marginBottom: '10px' }}>¡Pedido Recibido!</h3>
                                         {orderConfirmation && (
                                             <>
                                                 <div style={{ background: '#f0f7ff', border: '1px solid #3483fa', borderRadius: '8px', padding: '16px', margin: '16px 0', fontFamily: 'monospace' }}>
                                                     <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Código de pedido</div>
                                                     <div style={{ fontSize: '28px', fontWeight: '800', color: '#3483fa', letterSpacing: '6px' }}>{orderConfirmation.code}</div>
                                                 </div>
                                                 {orderConfirmation.paymentMethod !== 'mercadopago' && (
                                                     <div style={{ marginTop: '20px', marginBottom: '20px' }}>
                                                         <p style={{ color: '#2b7a78', fontWeight: '600', fontSize: '14px', marginBottom: '15px' }}>
                                                             👉 Para finalizar y coordinar el pedido, haz clic en el botón de abajo para enviarlo por WhatsApp.
                                                         </p>
                                                         <a
                                                             href={getWhatsAppOrderLink()}
                                                             target="_blank"
                                                             rel="noopener noreferrer"
                                                             style={{
                                                                 display: 'inline-flex',
                                                                 alignItems: 'center',
                                                                 justifyContent: 'center',
                                                                 gap: '10px',
                                                                 backgroundColor: '#25d366',
                                                                 color: '#fff',
                                                                 padding: '12px 20px',
                                                                 borderRadius: '8px',
                                                                 fontWeight: 'bold',
                                                                 textDecoration: 'none',
                                                                 boxShadow: '0 4px 12px rgba(37, 211, 102, 0.3)',
                                                                 fontSize: '16px'
                                                             }}
                                                         >
                                                             <FaWhatsapp size={20} /> Enviar pedido por WhatsApp
                                                         </a>
                                                     </div>
                                                 )}
                                             </>
                                         )}
                                         <p style={{ color: '#666', fontSize: '14px' }}>Tu pedido fue registrado. Nos comunicaremos a la brevedad para coordinar la entrega y el pago.</p>
                                     </div>
                                )}
                            </div>

                            <div style={styles.cartFooter}>
                                <div style={styles.totalRow}>
                                    <span>Total:</span>
                                    <span>${(checkoutStep === 'success' && orderConfirmation) ? orderConfirmation.total.toLocaleString() : cartTotal.toLocaleString()}</span>
                                </div>

                                {minPurchaseAmount > 0 && cartTotal < minPurchaseAmount && checkoutStep === 'cart' && (
                                    <div style={{
                                        padding: '10px 15px',
                                        background: '#fff3cd',
                                        border: '1px solid #ffc107',
                                        borderRadius: '6px',
                                        marginBottom: '10px',
                                        fontSize: '13px',
                                        color: '#856404'
                                    }}>
                                        ⚠️ Compra mínima: ${minPurchaseAmount.toLocaleString('es-AR')}.
                                        Te faltan ${(minPurchaseAmount - cartTotal).toLocaleString('es-AR')} para continuar.
                                    </div>
                                )}

                                {checkoutStep === 'cart' && (
                                    <button
                                        style={{
                                            ...styles.meliActionBtn,
                                            backgroundColor: buttonColor,
                                            opacity: (cart.length === 0 || (minPurchaseAmount > 0 && cartTotal < minPurchaseAmount)) ? 0.5 : 1,
                                            cursor: (cart.length === 0 || (minPurchaseAmount > 0 && cartTotal < minPurchaseAmount)) ? 'not-allowed' : 'pointer'
                                        }}
                                        disabled={cart.length === 0 || (minPurchaseAmount > 0 && cartTotal < minPurchaseAmount)}
                                        onClick={() => setCheckoutStep('shipping')}
                                    >
                                        Continuar compra
                                    </button>
                                )}
                                {checkoutStep === 'shipping' && (
                                    <div style={{ display: 'flex', gap: '10px' }}>
                                        <button style={{ ...styles.actionBtn, background: '#eee', color: '#333' }} onClick={() => setCheckoutStep('cart')}>Volver</button>
                                        <button style={{ ...styles.meliActionBtn, backgroundColor: buttonColor }} onClick={() => setCheckoutStep('customer')}>Siguiente</button>
                                    </div>
                                )}
                                {checkoutStep === 'customer' && (
                                    <div style={{ display: 'flex', gap: '10px' }}>
                                        <button style={{ ...styles.actionBtn, background: '#eee', color: '#333' }} onClick={() => { setFormError(''); setCheckoutStep('shipping'); }}>Volver</button>
                                        <button
                                            style={{ ...styles.meliActionBtn, backgroundColor: buttonColor }}
                                            onClick={handleGoToPayment}
                                        >
                                            Ir al pago
                                        </button>
                                    </div>
                                )}
                                {checkoutStep === 'payment' && (
                                    <div style={{ display: 'flex', gap: '10px' }}>
                                        <button style={{ ...styles.actionBtn, background: '#eee', color: '#333' }} onClick={() => setCheckoutStep('customer')}>Volver</button>
                                        <button
                                            style={{ ...styles.meliActionBtn, background: paymentMethod === 'mercadopago' ? buttonColor : '#22c55e' }}
                                            disabled={isProcessing} onClick={handleCheckout}
                                        >
                                            {isProcessing ? 'Procesando...' : paymentMethod === 'mercadopago' ? 'Pagar ahora' : 'Confirmar pedido'}
                                        </button>
                                    </div>
                                )}
                                {checkoutStep === 'success' && (
                                    <button style={{ ...styles.meliActionBtn, backgroundColor: buttonColor }} onClick={() => { setIsCartOpen(false); setCheckoutStep('cart'); setOrderConfirmation(null); }}>Seguir navegando</button>
                                )}
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence >

            {/* Product Details Full Screen Modal */}
            < AnimatePresence >
                {showDetails && selectedProduct && (
                    <motion.div
                        initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }}
                        transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                        style={styles.detailsModalFullScreen}
                    >
                        <div style={{ ...styles.detailsHeader, padding: isMobile ? '10px 15px' : '15px 20px' }}>
                            <button style={styles.backBtn} onClick={() => setShowDetails(false)}>
                                {isMobile ? '✕' : '← Volver al listado'}
                            </button>
                            <span style={{ fontWeight: 600, color: '#2d3277', fontSize: isMobile ? '14px' : '16px' }}>{shopData.storeName}</span>
                            <div style={styles.cartBtn} onClick={() => { setShowDetails(false); setIsCartOpen(true); }}>
                                <FaShoppingCart />
                                {cartCount > 0 && <span style={styles.badge}>{cartCount}</span>}
                            </div>
                        </div>

                        <div style={{ ...styles.detailsContainer, padding: isMobile ? '20px 15px' : '40px 20px' }}>
                            <div style={{ ...styles.detailsLayout, flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? '20px' : '60px' }}>
                                <div style={{ ...styles.detailsGallery, flex: isMobile ? '1 1 100%' : '1 1 500px' }}>
                                    <div
                                        style={{ ...styles.detailsMainImg, height: isMobile ? '300px' : '500px' }}
                                        onClick={() => {
                                            const allImgs = [selectedProduct.image, ...(selectedProduct.ecommerceImages || [])].filter(Boolean);
                                            const localOnly = allImgs.filter(img => !img.startsWith('http'));
                                            const uniqueImgs = [...new Set(localOnly)];
                                            setLightboxImage(uniqueImgs[activeImgIdx]);
                                            setShowLightbox(true);
                                        }}
                                    >
                                        <img
                                            src={getOptimizedUrl((() => {
                                                const allImgs = [selectedProduct.image, ...(selectedProduct.ecommerceImages || [])].filter(Boolean);
                                                const localOnly = allImgs.filter(img => !img.startsWith('http'));
                                                const uniqueImgs = [...new Set(localOnly)];
                                                return uniqueImgs[activeImgIdx];
                                            })(), 800) || '/placeholder.png'}
                                            alt={selectedProduct.name}
                                            style={{ width: '100%', height: '100%', objectFit: 'scale-down', cursor: 'zoom-in' }}
                                            onError={(e) => {
                                                if (e.target.src !== selectedProduct.image && selectedProduct.image) {
                                                    e.target.src = selectedProduct.image;
                                                } else if (e.target.src !== 'https://via.placeholder.com/600?text=Sin+Imagen') {
                                                    e.target.src = 'https://via.placeholder.com/600?text=Sin+Imagen';
                                                }
                                            }}
                                        />
                                        <div style={styles.magnifyHint}>Clic para ampliar</div>
                                    </div>
                                    {(selectedProduct.image || selectedProduct.ecommerceImages?.length > 0) && (
                                        <div style={styles.detailsThumbGrid}>
                                            {[...new Set([selectedProduct.image, ...(selectedProduct.ecommerceImages || [])].filter(img => img && !img.startsWith('http')))].map((img, idx) => (
                                                <img
                                                    key={idx} src={getOptimizedUrl(img, 100)} alt=""
                                                    style={{
                                                        ...styles.detailsThumb,
                                                        border: activeImgIdx === idx ? '2px solid #3483fa' : '1px solid #eee'
                                                    }}
                                                    onClick={() => setActiveImgIdx(idx)}
                                                />
                                            ))}
                                        </div>
                                    )}
                                </div>

                                <div style={{ ...styles.detailsInfo, flex: isMobile ? '1 1 100%' : '1 1 400px' }}>
                                    <span style={{ fontSize: '13px', color: '#666', textTransform: 'uppercase', letterSpacing: '1px' }}>{selectedProduct.category}</span>
                                    <h2 style={{ fontSize: isMobile ? '24px' : '32px', margin: '10px 0 20px 0', fontWeight: '600', color: '#333' }}>{selectedProduct.name}</h2>

                                    <div style={{ marginBottom: '30px', padding: isMobile ? '15px' : '25px', border: '1px solid #eee', borderRadius: '12px' }}>
                                        <div style={{ ...styles.detailsPrice, fontSize: isMobile ? '32px' : '40px' }}>
                                            $ {getDisplayPrice(selectedProduct, selectedVariant).toLocaleString()}
                                        </div>
                                        <p style={{ ...styles.meliEnvio, fontSize: isMobile ? '14px' : '16px', margin: '10px 0' }}>Envío GRATIS por la zona</p>

                                        {selectedProduct.variants && selectedProduct.variants.length > 0 && (
                                            <div style={{ margin: '20px 0' }}>
                                                <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', marginBottom: '10px', color: '#333' }}>
                                                    Selecciona tu opción:
                                                </label>
                                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                                    {selectedProduct.variants.map(v => (
                                                        <button
                                                            key={v._id}
                                                            onClick={() => setSelectedVariant(v)}
                                                            style={{
                                                                padding: '10px 16px',
                                                                borderRadius: '8px',
                                                                border: selectedVariant?._id === v._id ? '2px solid #3483fa' : '1px solid #ddd',
                                                                background: selectedVariant?._id === v._id ? '#e3f2fd' : '#fff',
                                                                color: selectedVariant?._id === v._id ? '#3483fa' : '#333',
                                                                fontSize: '14px',
                                                                fontWeight: selectedVariant?._id === v._id ? '600' : '400',
                                                                cursor: (v.stock > 0 || selectedProduct.noControlStock) ? 'pointer' : 'not-allowed',
                                                                opacity: (v.stock > 0 || selectedProduct.noControlStock) ? 1 : 0.5,
                                                                transition: 'all 0.2s',
                                                                minWidth: '60px',
                                                                textAlign: 'center'
                                                            }}
                                                            disabled={!selectedProduct.noControlStock && v.stock <= 0}
                                                        >
                                                            {[v.attributes?.color, v.attributes?.size, v.attributes?.style].filter(Boolean).join(' / ') || v.sku}
                                                            {(!selectedProduct.noControlStock && v.stock <= 0) && ' (Sin stock)'}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        <div style={{ margin: '20px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <div style={{
                                                width: '8px', height: '8px', borderRadius: '50%',
                                                backgroundColor: (selectedVariant ? selectedVariant.stock : selectedProduct.stock) > 0 ? '#00a650' : '#ef4444'
                                            }}></div>
                                            <span style={{ fontSize: isMobile ? '13px' : '15px', color: '#444' }}>
                                                {(selectedVariant ? selectedVariant.stock : selectedProduct.stock) > 0
                                                    ? `Stock disponible (${selectedVariant ? selectedVariant.stock : selectedProduct.stock}u)`
                                                    : 'Sin stock disponible'}
                                            </span>
                                        </div>

                                        {/* Selector de cantidad */}
                                        {(selectedProduct.noControlStock || (selectedVariant ? selectedVariant.stock : selectedProduct.stock) > 0) && (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', margin: '0 0 18px 0' }}>
                                                <span style={{ fontSize: '14px', fontWeight: '600', color: '#555' }}>Cantidad:</span>
                                                <div style={{
                                                    display: 'flex', alignItems: 'center', gap: '0',
                                                    border: '1.5px solid #ddd', borderRadius: '8px', overflow: 'hidden'
                                                }}>
                                                    <button
                                                        onClick={() => setDetailQty(q => Math.max(1, q - 1))}
                                                        style={{
                                                            width: isMobile ? '36px' : '42px',
                                                            height: isMobile ? '36px' : '42px',
                                                            border: 'none', background: '#f5f5f5',
                                                            fontSize: '20px', fontWeight: '700',
                                                            cursor: detailQty <= 1 ? 'not-allowed' : 'pointer',
                                                            color: detailQty <= 1 ? '#ccc' : '#333',
                                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                            transition: 'background 0.15s'
                                                        }}
                                                        disabled={detailQty <= 1}
                                                    >−</button>
                                                    <input
                                                        type="number"
                                                        value={detailQty}
                                                        min={1}
                                                        max={selectedProduct.noControlStock ? 9999 : (selectedVariant ? selectedVariant.stock : selectedProduct.stock)}
                                                        onChange={e => {
                                                            const val = parseInt(e.target.value, 10);
                                                            const maxStock = selectedProduct.noControlStock ? 9999 : (selectedVariant ? selectedVariant.stock : selectedProduct.stock);
                                                            if (!isNaN(val) && val >= 1 && val <= maxStock) setDetailQty(val);
                                                        }}
                                                        style={{
                                                            width: isMobile ? '44px' : '52px',
                                                            height: isMobile ? '36px' : '42px',
                                                            border: 'none', borderLeft: '1.5px solid #ddd', borderRight: '1.5px solid #ddd',
                                                            textAlign: 'center', fontSize: '16px', fontWeight: '600',
                                                            color: '#333', outline: 'none', background: '#fff',
                                                            MozAppearance: 'textfield'
                                                        }}
                                                    />
                                                    <button
                                                        onClick={() => {
                                                            const maxStock = selectedProduct.noControlStock ? 9999 : (selectedVariant ? selectedVariant.stock : selectedProduct.stock);
                                                            setDetailQty(q => Math.min(maxStock, q + 1));
                                                        }}
                                                        style={{
                                                            width: isMobile ? '36px' : '42px',
                                                            height: isMobile ? '36px' : '42px',
                                                            border: 'none', background: '#f5f5f5',
                                                            fontSize: '20px', fontWeight: '700',
                                                            cursor: 'pointer', color: '#333',
                                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                            transition: 'background 0.15s'
                                                        }}
                                                        disabled={!selectedProduct.noControlStock && detailQty >= (selectedVariant ? selectedVariant.stock : selectedProduct.stock)}
                                                    >+</button>
                                                </div>
                                            </div>
                                        )}

                                        <motion.button
                                            whileHover={{ scale: 1.02 }}
                                            whileTap={{ scale: 0.97, backgroundColor: '#2b6fd2' }}
                                            style={{
                                                ...styles.meliActionBtn,
                                                height: isMobile ? '50px' : '60px',
                                                borderRadius: '8px',
                                                fontSize: isMobile ? '16px' : '18px',
                                                background: (
                                                    (selectedProduct.variants?.length > 0 && !selectedVariant) ||
                                                    (!selectedProduct.noControlStock && (selectedVariant ? selectedVariant.stock : selectedProduct.stock) <= 0)
                                                ) ? '#ccc' : buttonColor || '#3483fa',
                                                cursor: (
                                                    (selectedProduct.variants?.length > 0 && !selectedVariant) ||
                                                    (!selectedProduct.noControlStock && (selectedVariant ? selectedVariant.stock : selectedProduct.stock) <= 0)
                                                ) ? 'not-allowed' : 'pointer'
                                            }}
                                            disabled={(selectedProduct.variants?.length > 0 && !selectedVariant) || (!selectedProduct.noControlStock && (selectedVariant ? selectedVariant.stock : selectedProduct.stock) <= 0)}
                                            onClick={() => { addToCart(selectedProduct, selectedVariant, detailQty); setShowDetails(false); setDetailQty(1); }}
                                        >
                                            {selectedProduct.variants?.length > 0 && !selectedVariant
                                                ? 'Selecciona una opción'
                                                : ((!selectedProduct.noControlStock && (selectedVariant ? selectedVariant.stock : selectedProduct.stock) <= 0) ? 'Sin stock' : `Agregar ${detailQty > 1 ? detailQty + ' ' : ''}al carrito`)}
                                        </motion.button>
                                        <p style={{ textAlign: 'center', fontSize: '13px', color: '#999', marginTop: '15px' }}>Compra protegida · Recibirás lo que esperabas</p>
                                    </div>

                                    {selectedProduct.ecommerceDescription && (
                                        <div style={{ marginBottom: '40px' }}>
                                            <h4 style={{ marginBottom: '15px', fontSize: '18px', fontWeight: '700', color: '#333', borderBottom: '2px solid #fff159', display: 'inline-block', paddingBottom: '3px' }}>
                                                Descripción del producto
                                            </h4>
                                            <p style={{ fontSize: '16px', color: '#666', lineHeight: '1.8', whiteSpace: 'pre-line' }}>{selectedProduct.ecommerceDescription}</p>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Sugerencias estilo Mercado Libre */}
                            <div style={styles.suggestionsSection}>
                                <h3 style={{ fontSize: isMobile ? '18px' : '22px', marginBottom: '30px', fontWeight: '600', color: '#333' }}>Productos que también te pueden interesar</h3>
                                <div style={{
                                    ...styles.suggestionGrid,
                                    gridTemplateColumns: isMobile ? 'repeat(auto-fill, minmax(140px, 1fr))' : 'repeat(auto-fill, minmax(180px, 1fr))',
                                    gap: isMobile ? '10px' : '20px'
                                }}>
                                    {shopData.products
                                        .filter(p => p._id !== selectedProduct._id)
                                        .sort((a) => (a.category === selectedProduct.category ? -1 : 1))
                                        .slice(0, 5)
                                        .map(p => (
                                            <div
                                                key={p._id}
                                                style={styles.suggestionCard}
                                                onClick={() => {
                                                    setSelectedProduct(p);
                                                    setSelectedVariant(null);
                                                    const container = document.querySelector('[class*="detailsModalFullScreen"]');
                                                    if (container) container.scrollTo({ top: 0, behavior: 'smooth' });
                                                    else window.scrollTo({ top: 0, behavior: 'smooth' });
                                                }}
                                            >
                                                <div style={{ height: isMobile ? '130px' : '170px', position: 'relative', background: '#fff', padding: '10px' }}>
                                                    <img
                                                        src={getOptimizedUrl(getPrioritizedImageUrl(p), 400)}
                                                        alt=""
                                                        style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                                        loading="lazy"
                                                    />
                                                </div>
                                                <div style={{ padding: isMobile ? '10px' : '15px' }}>
                                                    <div style={{ fontSize: isMobile ? '16px' : '20px', fontWeight: '500', color: '#333' }}>$ {getDisplayPrice(p).toLocaleString()}</div>
                                                    <div style={{ fontSize: '12px', color: '#00a650', fontWeight: '700', marginTop: '4px' }}>Envío GRATIS</div>
                                                    <div style={{ fontSize: isMobile ? '12px' : '14px', color: '#666', marginTop: '8px', height: '38px', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                                                        {p.name}
                                                    </div>
                                                </div>
                                            </div>
                                        ))
                                    }
                                </div>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence >

            {/* Floating Cart Button (Mobile & Desktop with Items) */}
            <AnimatePresence>
                {cartCount > 0 && !isCartOpen && (
                    <motion.div
                        initial={{ scale: 0, y: 20 }}
                        animate={{ scale: 1, y: 0 }}
                        exit={{ scale: 0, y: 20 }}
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        style={{
                            position: 'fixed',
                            bottom: '30px',
                            right: '30px',
                            zIndex: 1000,
                            background: '#3483fa',
                            color: '#fff',
                            width: '65px',
                            height: '65px',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '28px',
                            boxShadow: '0 8px 30px rgba(52, 131, 250, 0.4)',
                            cursor: 'pointer'
                        }}
                        onClick={() => setIsCartOpen(true)}
                    >
                        <FaShoppingCart />
                        <span style={{
                            position: 'absolute',
                            top: '0',
                            right: '0',
                            background: '#ff5a5f',
                            color: '#fff',
                            fontSize: '14px',
                            fontWeight: '900',
                            width: '24px',
                            height: '24px',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: '2px solid #fff'
                        }}>
                            {cartCount}
                        </span>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Lightbox for high quality view */}
            <AnimatePresence>
                {showLightbox && (
                    <motion.div
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        style={styles.lightboxOverlay}
                        onClick={() => setShowLightbox(false)}
                    >
                        <button style={{ ...styles.lightboxClose, top: isMobile ? '10px' : '20px', right: isMobile ? '10px' : '25px', padding: isMobile ? '8px 15px' : '10px 20px' }}>
                            {isMobile ? '✕' : '✕ Cerrar'}
                        </button>
                        <motion.img
                            initial={{ scale: 0.8, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.8, opacity: 0 }}
                            src={getOptimizedUrl(lightboxImage, 1200)}
                            alt="Vista ampliada"
                            style={styles.lightboxImg}
                            onClick={(e) => e.stopPropagation()}
                        />
                    </motion.div>
                )}
            </AnimatePresence >
            {/* Floating WhatsApp Button */}
            {shopData.settings?.whatsapp && (
                <motion.a
                    href={`https://wa.me/${whatsappLink}`}
                    target="_blank"
                    rel="noreferrer"
                    style={styles.floatingWhatsapp}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ type: 'spring', stiffness: 260, damping: 20, delay: 1 }}
                >
                    <FaWhatsapp size={32} />
                </motion.a>
            )}

            {/* Footer */}
            <footer style={styles.footer}>
                <div style={styles.footerContent}>
                    <div style={styles.footerCol}>
                        <div style={styles.footerBrand}>
                            <h2 style={{ ...styles.meliLogo, color: '#333', fontSize: '24px', marginBottom: '10px' }}>{shopData.storeName}</h2>
                            <p style={{ fontSize: '14px', color: '#666', lineHeight: '1.6' }}>
                                Tu tienda de confianza para encontrar los mejores productos al mejor precio.
                                Servicio de atención personalizada y envíos rápidos.
                            </p>
                        </div>
                    </div>

                    <div style={styles.footerCol}>
                        <h3 style={styles.footerTitle}>Contacto</h3>
                        <div style={styles.footerInfo}>
                            {shopData.settings?.address && (
                                <a 
                                    href={shopData.storeName?.toUpperCase() === 'HANNA' ? 'https://www.google.com/maps/place/Hanna+Bazar+%26+Deco/@-34.4983464,-58.6409396,17.75z/data=!4m14!1m7!3m6!1s0x95bcbb513ebebfd3:0xaca2acb9226fc2f3!2sColegio+Parroquial+San+Marcelo!8m2!3d-34.4984218!4d-58.6396745!16s%2Fg%2F11j2yqh954!3m5!1s0x95bcbb002d56b71d:0x6bb5ffb63f69b641!8m2!3d-34.4977586!4d-58.639283!16s%2Fg%2F11n3wxs53r' : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(shopData.settings.address)}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    style={{ ...styles.footerItem, textDecoration: 'none', color: 'inherit', cursor: 'pointer' }}
                                    title="Ver en Google Maps"
                                >
                                    <div style={styles.iconCircle}><FaMapMarkerAlt size={14} /></div>
                                    <div style={styles.footerItemText}>
                                        <span style={styles.footerLabel}>Ubicación</span>
                                        <span>{shopData.settings.address}</span>
                                    </div>
                                </a>
                            )}
                            {shopData.settings?.phone && (
                                <div style={styles.footerItem}>
                                    <div style={styles.iconCircle}><FaPhoneAlt size={14} /></div>
                                    <div style={styles.footerItemText}>
                                        <span style={styles.footerLabel}>Llamanos</span>
                                        <span>{shopData.settings.phone}</span>
                                    </div>
                                </div>
                            )}
                            {shopData.settings?.whatsapp && (
                                <div style={styles.footerItem}>
                                    <div style={styles.iconCircle}><FaWhatsapp size={14} /></div>
                                    <div style={styles.footerItemText}>
                                        <span style={styles.footerLabel}>WhatsApp</span>
                                        <span>{shopData.settings.whatsapp}</span>
                                    </div>
                                </div>
                            )}
                        </div>
                        {/* Mapa Visual */}
                        {shopData.settings?.address && (
                            <div style={{ marginTop: '20px', borderRadius: '12px', overflow: 'hidden', border: '1px solid #eee' }}>
                                <iframe
                                    title="Mapa de Ubicación"
                                    width="100%"
                                    height="180"
                                    style={{ border: 0, display: 'block' }}
                                    loading="lazy"
                                    allowFullScreen
                                    src={shopData.storeName?.toUpperCase() === 'HANNA' ? 'https://www.google.com/maps?q=-34.4977586,-58.639283&output=embed' : `https://www.google.com/maps?q=${encodeURIComponent(shopData.settings.address)}&output=embed`}
                                ></iframe>
                            </div>
                        )}
                    </div>

                    <div style={styles.footerCol}>
                        <h3 style={styles.footerTitle}>Horarios</h3>
                        <p style={{ fontSize: '14px', color: '#666', lineHeight: '1.6', whiteSpace: 'pre-line' }}>
                            {shopData.settings?.businessHours || 'Lunes a Viernes: 09:00 - 18:00\nSábados: 09:00 - 13:00'}
                        </p>
                        <div style={{ marginTop: '20px', display: 'flex', gap: '15px' }}>
                            <div style={styles.socialIcon}><FaWhatsapp size={18} /></div>
                            <div style={styles.socialIcon}><FaEnvelope size={18} /></div>
                        </div>
                    </div>

                    <div style={{
                        gridColumn: '1 / -1',
                        marginTop: '30px',
                        paddingTop: '20px',
                        borderTop: '1px solid #f0f0f0',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '12px',
                        alignItems: 'center'
                    }}>
                        <span style={{ fontSize: '12px', fontWeight: '700', color: '#999', textTransform: 'uppercase', letterSpacing: '1px' }}>
                            Medios de Pago
                        </span>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px', justifyContent: 'center', alignItems: 'center' }}>
                            {/* Mercado Pago */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', background: '#fff', padding: '6px 12px', borderRadius: '6px', border: '1px solid #eee', fontSize: '13px', fontWeight: '600', color: '#009EE3' }}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 15h-2v-6h2v6zm0-8h-2V7h2v2z" />
                                </svg>
                                Mercado Pago
                            </div>
                            {/* Transferencia */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', background: '#fff', padding: '6px 12px', borderRadius: '6px', border: '1px solid #eee', fontSize: '13px', fontWeight: '600', color: '#00b981' }}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
                                    <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
                                </svg>
                                Transferencia
                            </div>
                            {/* Tarjeta de Crédito */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', background: '#fff', padding: '6px 12px', borderRadius: '6px', border: '1px solid #eee', fontSize: '13px', fontWeight: '600', color: '#4b5563' }}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <rect x="2" y="5" width="20" height="14" rx="2" />
                                    <line x1="2" y1="10" x2="22" y2="10" />
                                </svg>
                                Tarjeta de Crédito
                            </div>
                            {/* Tarjeta de Débito */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', background: '#fff', padding: '6px 12px', borderRadius: '6px', border: '1px solid #eee', fontSize: '13px', fontWeight: '600', color: '#4b5563' }}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <rect x="2" y="5" width="20" height="14" rx="2" />
                                    <line x1="2" y1="10" x2="22" y2="10" />
                                    <line x1="6" y1="15" x2="10" y2="15" />
                                </svg>
                                Tarjeta de Débito
                            </div>
                        </div>

                        <span style={{ fontSize: '12px', fontWeight: '700', color: '#999', textTransform: 'uppercase', letterSpacing: '1px', marginTop: '10px' }}>
                            Métodos de Envío
                        </span>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px', justifyContent: 'center', alignItems: 'center' }}>
                            {/* Andreani */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', background: '#fff', padding: '6px 12px', borderRadius: '6px', border: '1px solid #eee', fontSize: '14px', fontWeight: '900', fontStyle: 'italic', color: '#E3000F', letterSpacing: '0.5px' }}>
                                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <rect x="1" y="3" width="15" height="13" rx="1" ry="1"></rect>
                                  <polygon points="16 8 20 8 23 11 23 16 16 16 16 8"></polygon>
                                  <circle cx="5.5" cy="18.5" r="2.5"></circle>
                                  <circle cx="18.5" cy="18.5" r="2.5"></circle>
                                </svg>
                                ANDREANI
                            </div>
                        </div>
                    </div>

                    <div style={styles.footerBottom}>
                        <p>&copy; {new Date().getFullYear()} {shopData.storeName}. Todos los derechos reservados.</p>
                        <p>Plataforma de E-commerce impulsada por <a href="https://ventio.com.ar" target="_blank" rel="noreferrer" style={{ color: '#3483fa', textDecoration: 'none', fontWeight: '600' }}>Ventio</a></p>
                    </div>
                </div>
            </footer>
        </div >
    );
};

const styles = {
    container: { backgroundColor: '#f5f5f5', minHeight: '100vh', paddingBottom: '50px', fontFamily: "'Segoe UI', Roboto, Helvetica, Arial, sans-serif" },
    center: { display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', fontSize: '18px' },

    // MELI STYLE HEADER
    meliHeader: {
        backgroundColor: '#00CED1',
        padding: '15px 0',
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        position: 'sticky',
        top: 0,
        zIndex: 100,
        transition: 'all 0.3s ease'
    },
    topBar: {
        backgroundColor: '#333',
        color: '#fff',
        padding: '6px 0',
        fontSize: '12px',
        fontWeight: '500'
    },
    topBarContent: {
        maxWidth: '100%',
        margin: '0 auto',
        padding: '0 40px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
    },
    topBarItems: {
        display: 'flex',
        gap: '20px',
        flexWrap: 'wrap'
    },
    topBarItem: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        opacity: 0.9
    },
    topBarRight: {
        display: window.innerWidth < 768 ? 'none' : 'block'
    },
    headerLimit: { maxWidth: '100%', margin: '0 auto', padding: '0 40px' },
    headerTop: { display: 'flex', alignItems: 'center', gap: '20px', justifyContent: 'space-between' },
    meliLogo: { fontSize: '26px', fontWeight: '900', color: '#2d3277', margin: 0, cursor: 'default', letterSpacing: '-0.5px' },
    searchContainer: { flex: 1, maxWidth: '600px', position: 'relative' },
    meliSearch: {
        width: '100%',
        padding: '14px 25px',
        borderRadius: '12px',
        border: '1px solid rgba(0,0,0,0.05)',
        boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
        fontSize: '16px',
        outline: 'none',
        transition: 'all 0.3s ease',
        backgroundColor: '#fff'
    },

    // HERO / BANNER - Premium Entry Point
    heroBanner: {
        height: '140px',
        backgroundColor: '#1a1c3d',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#fff',
        textAlign: 'center',
        position: 'relative'
    },
    bannerText: {
        maxWidth: '900px',
        padding: '20px',
        textShadow: '0 4px 15px rgba(0,0,0,0.6)',
    },

    mainContent: { maxWidth: '100%', margin: '0 auto', padding: window.innerWidth < 768 ? '0 10px' : '0 40px' },

    shopLayout: { display: 'flex', gap: '30px', alignItems: 'flex-start', marginTop: '20px' },
    sidebar: { width: '240px', flexShrink: 0, position: 'sticky', top: '100px', background: '#fff', borderRadius: '12px', padding: '20px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' },
    sidebarTitle: { fontSize: '18px', fontWeight: '700', color: '#333', marginBottom: '20px', paddingBottom: '10px', borderBottom: '1px solid #eee' },
    sidebarLink: { display: 'flex', width: '100%', alignItems: 'center', padding: '12px 0', border: 'none', background: 'none', cursor: 'pointer', fontSize: '15px', color: '#666', transition: 'all 0.2s', textAlign: 'left' },
    sidebarLinkActive: { color: '#3483fa', fontWeight: '700' },
    contentArea: { flex: 1, minWidth: 0 },

    cartBtn: { position: 'relative', fontSize: '22px', cursor: 'pointer', color: '#333' },
    badge: { position: 'absolute', top: '-10px', right: '-10px', background: '#3483fa', color: '#fff', fontSize: '11px', borderRadius: '10px', padding: '2px 5px', fontWeight: 'bold' },

    filterBar: { display: 'flex', gap: '20px', padding: '10px 0', overflowX: 'auto', marginBottom: '20px' },
    filterBtn: { background: 'none', border: 'none', color: '#333', cursor: 'pointer', fontSize: '14px', whiteSpace: 'nowrap', opacity: 0.7 },
    filterBtnActive: { fontWeight: '700', borderBottom: '2px solid #3483fa', opacity: 1 },
    productGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '20px' },

    // MELI STYLE CARD REFINED
    meliCard: {
        background: '#fff',
        borderRadius: '8px',
        overflow: 'hidden',
        cursor: 'pointer',
        border: '1px solid #eee',
        transition: 'all 0.2s ease',
        display: 'flex',
        flexDirection: 'column',
        height: '100%'
    },
    imgContainer: {
        width: '100%',
        height: '220px',
        borderBottom: '1px solid #f0f0f0',
        position: 'relative',
        padding: '10px',
        backgroundColor: '#f8f8f8', // Gris muy claro para delimitar área de imagen
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },
    img: { maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' },
    cardBody: { padding: '15px', flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }, // Added justifyContent
    prodName: {
        fontSize: '14px', // Reduced slightly for mobile safety
        fontWeight: '500',
        color: '#333',
        marginBottom: '8px',
        height: '40px', // Adjusted height
        overflow: 'hidden',
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        lineHeight: '1.3'
    },
    meliPrice: { fontSize: '26px', fontWeight: '600', color: '#333', display: 'flex', alignItems: 'center', gap: '8px' },
    meliEnvio: { fontSize: '13px', fontWeight: '600', color: '#00a650', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px' },
    meliAddBtn: {
        width: '100%',
        padding: '10px',
        marginTop: '15px',
        background: '#3483fa',
        color: '#fff',
        border: 'none',
        borderRadius: '6px',
        fontWeight: '600',
        cursor: 'pointer',
        fontSize: '14px',
        transition: 'background 0.2s'
    },

    // SIDEBAR & CHECKOUT
    overlay: { position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 200 },
    cartSidebar: { position: 'fixed', right: 0, top: 0, bottom: 0, width: '400px', maxWidth: '100%', backgroundColor: '#fff', zIndex: 201, display: 'flex', flexDirection: 'column', boxShadow: '-5px 0 15px rgba(0,0,0,0.1)' },
    cartHeader: { padding: '20px', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    closeBtn: { background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer' },
    cartContent: { flex: 1, padding: '20px', overflowY: 'auto' },
    cartItem: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', paddingBottom: '15px', borderBottom: '1px solid #eee' },
    cartFooter: { padding: '20px', borderTop: '1px solid #eee', backgroundColor: '#f9f9f9' },
    totalRow: { display: 'flex', justifyContent: 'space-between', fontSize: '18px', fontWeight: '600', marginBottom: '20px' },
    meliActionBtn: { width: '100%', background: '#3483fa', color: '#fff', border: 'none', padding: '15px', borderRadius: '6px', fontSize: '16px', fontWeight: '600', cursor: 'pointer' },
    actionBtn: { width: '100%', padding: '15px', borderRadius: '6px', border: 'none', cursor: 'pointer', fontWeight: '600' },
    checkoutSection: { display: 'flex', flexDirection: 'column', gap: '15px' },
    optionGroup: { display: 'flex', gap: '10px' },
    optionCard: { flex: 1, padding: '12px', border: '1px solid #ddd', borderRadius: '6px', cursor: 'pointer', textAlign: 'center', fontSize: '13px' },
    optionCardActive: { borderColor: '#3483fa', background: '#f0f5ff' },
    zoneCard: { display: 'flex', justifyContent: 'space-between', padding: '10px', border: '1px solid #eee', borderRadius: '4px', marginBottom: '5px', cursor: 'pointer', fontSize: '13px' },
    zoneCardActive: { background: '#f3f3f3', fontWeight: 'bold' },
    input: { padding: '12px', borderRadius: '4px', border: '1px solid #ddd', fontSize: '14px', width: '100%', boxSizing: 'border-box' },
    mapPreviewContainer: { marginTop: '15px', padding: '10px', backgroundColor: '#f9f9f9', borderRadius: '8px', border: '1px solid #eee' },

    detailsModalFullScreen: {
        position: 'fixed',
        inset: 0,
        backgroundColor: '#fff',
        zIndex: 202,
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column'
    },
    detailsHeader: {
        padding: '15px 20px',
        borderBottom: '1px solid #eee',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        position: 'sticky',
        top: 0,
        backgroundColor: '#fff',
        zIndex: 10
    },
    backBtn: { background: 'none', border: 'none', fontSize: '16px', color: '#3483fa', cursor: 'pointer', fontWeight: '600' },
    detailsContainer: { maxWidth: '1200px', margin: '0 auto', padding: '40px 20px', width: '100%', boxSizing: 'border-box' },
    detailsLayout: { display: 'flex', gap: '60px', flexWrap: 'wrap', marginBottom: '60px' },
    detailsGallery: { flex: '1 1 500px' },
    detailsMainImg: { width: '100%', height: '500px', backgroundColor: '#fff', borderRadius: '8px', marginBottom: '20px', border: '1px solid #f0f0f0' },
    detailsThumbGrid: { display: 'flex', gap: '10px', overflowX: 'auto', paddingBottom: '10px' },
    detailsThumb: { width: '80px', height: '80px', objectFit: 'contain', border: '1px solid #eee', borderRadius: '6px', cursor: 'pointer', flexShrink: 0 },
    detailsInfo: { flex: '1 1 400px' },
    detailsPrice: { fontSize: '40px', fontWeight: '300', color: '#333' },

    suggestionsSection: { borderTop: '1px solid #eee', paddingTop: '40px', marginTop: '40px' },
    suggestionGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '20px' },
    suggestionCard: { background: '#fff', borderRadius: '8px', border: '1px solid #eee', overflow: 'hidden', cursor: 'pointer', transition: 'box-shadow 0.2s' },
    suggestionImgBox: { height: '180px', borderBottom: '1px solid #f5f5f5', padding: '10px' },

    // LIGHTBOX
    lightboxOverlay: {
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0,0,0,0.9)',
        zIndex: 300,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'zoom-out'
    },
    lightboxImg: {
        maxWidth: '95%',
        maxHeight: '95vh',
        objectFit: 'contain',
        boxShadow: '0 0 30px rgba(0,0,0,0.5)'
    },
    lightboxClose: {
        position: 'absolute',
        top: '20px',
        right: '25px',
        background: 'rgba(255,255,255,0.2)',
        color: '#fff',
        border: 'none',
        padding: '10px 20px',
        borderRadius: '30px',
        cursor: 'pointer',
        fontSize: '16px'
    },
    magnifyHint: {
        position: 'absolute',
        bottom: '10px',
        right: '10px',
        background: 'rgba(255,255,255,0.8)',
        padding: '4px 10px',
        borderRadius: '4px',
        fontSize: '11px',
        color: '#666',
        pointerEvents: 'none',
        border: '1px solid #ddd'
    },

    // FOOTER REFINED
    footer: {
        backgroundColor: '#fff',
        padding: '60px 0 30px',
        marginTop: '80px',
        borderTop: '1px solid #eee',
        boxShadow: '0 -10px 30px rgba(0,0,0,0.02)'
    },
    footerContent: {
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '0 40px',
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: '40px'
    },
    footerCol: {
        display: 'flex',
        flexDirection: 'column'
    },
    footerTitle: {
        fontSize: '16px',
        fontWeight: '700',
        color: '#333',
        marginBottom: '20px',
        textTransform: 'uppercase',
        letterSpacing: '1px'
    },
    footerInfo: {
        display: 'flex',
        flexDirection: 'column',
        gap: '20px'
    },
    footerItem: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px'
    },
    iconCircle: {
        minWidth: '32px',
        height: '32px',
        borderRadius: '50%',
        backgroundColor: '#f0f5ff',
        color: '#3483fa',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },
    footerItemText: {
        display: 'flex',
        flexDirection: 'column',
        fontSize: '14px',
        color: '#555'
    },
    footerLabel: {
        fontSize: '11px',
        fontWeight: '700',
        color: '#999',
        textTransform: 'uppercase',
        marginBottom: '2px'
    },
    socialIcon: {
        width: '40px',
        height: '40px',
        borderRadius: '8px',
        backgroundColor: '#f8f9fa',
        color: '#333',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        transition: 'all 0.2s',
        border: '1px solid #eee'
    },
    footerBottom: {
        gridColumn: '1 / -1',
        marginTop: '40px',
        paddingTop: '25px',
        borderTop: '1px solid #f0f0f0',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '15px',
        fontSize: '13px',
        color: '#999'
    },

    // FLOATING WHATSAPP
    floatingWhatsapp: {
        position: 'fixed',
        bottom: '30px',
        right: '30px',
        width: '60px',
        height: '60px',
        backgroundColor: '#25d366',
        color: '#fff',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 4px 15px rgba(37, 211, 102, 0.4)',
        zIndex: 1000,
        textDecoration: 'none',
        cursor: 'pointer'
    }
};

export default PublicShop;
